import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "wouter";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";
import { GoogleMap, useJsApiLoader, OverlayViewF, OverlayView } from "@react-google-maps/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { trackEvent } from "@/hooks/useTracking";
import { useLocationStore } from "@/lib/store";
import type { Location } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Settings2,
  Send,
  Plus,
  X,
  Mail,
  MessageSquare,
  Calendar,
  ChevronDown,
  RefreshCw,
  Link2,
  Check,
  Copy,
  TrendingUp,
  Globe,
  Phone,
  MapPin,
  MousePointerClick,
  Save,
  FileDown,
} from "lucide-react";
import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

function GoogleMapsProvider({ apiKey, children }: { apiKey: string; children: React.ReactNode }) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey, id: "google-maps-script" });
  const [mapsAuthFailed, setMapsAuthFailed] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // A failed Maps key auth (referrer-restricted, disabled, billing off, API not
  // enabled) leaves the loader promise hanging — isLoaded stays false and
  // loadError stays undefined. Catch the global gm_authFailure callback plus a
  // hard timeout so we show "Map unavailable" instead of an endless "Loading map…".
  useEffect(() => {
    const prev = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => setMapsAuthFailed(true);
    return () => {
      (window as any).gm_authFailure = prev;
    };
  }, []);

  useEffect(() => {
    if (isLoaded || loadError) return;
    const t = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, [isLoaded, loadError]);

  if (loadError || mapsAuthFailed || (loadTimedOut && !isLoaded)) return <div className="w-full h-full bg-[#0d1117] flex items-center justify-center"><div className="text-white/20 text-sm">Map unavailable</div></div>;
  if (!isLoaded) return <div className="w-full h-full bg-[#0d1117] flex items-center justify-center"><div className="text-white/20 text-sm">Loading map…</div></div>;
  return <>{children}</>;
}

interface PeriodData {
  label: string;
  views: number;
  calls: number;
  directions: number;
  clicks: number;
  actions: number;
  searchImpressions: number;
  mapsImpressions: number;
  avgRating: number;
  totalReviews: number;
  newReviews: number;
  responseRate: number;
  keywords: number;
  avgRank: number;
  avgVisibility: number;
  keywordsInTop3: number;
  reviewsLast90Days: number;
  profileCompleteness: number;
  posts: number;
  photos: number;
  tokens: number;
}

interface KeywordRow {
  name: string;
  rank: number;
  visibility: number;
  prev: number;
}

interface ReviewRow {
  author: string;
  date: string;
  rating: number;
  snippet: string;
}

interface WinRow {
  text: string;
  icon: string;
  time: string;
  delta: string;
}

interface ClientInfo {
  name: string;
  category: string;
  address: string;
  phone: string;
  monthsActive: number;
  plan: string;
  stage: number;
}

interface GridPointInfo {
  lat: number;
  lng: number;
  rank: number;
  position: string;
}

interface GridInfo {
  keyword: string;
  avgRank: number;
  visibility: number;
  prevVisibility: number;
  scannedAt: string;
  gridData: number[][];
  centerLat: number;
  centerLng: number;
  points: GridPointInfo[];
}

interface TrafficTrendPoint {
  label: string;
  views: number;
  calls: number;
  directions: number;
  clicks: number;
  searchImpressions: number;
  mapsImpressions: number;
}

interface CompetitorRow {
  name: string;
  winsAgainst: number;
  lossesAgainst: number;
  totalPoints: number;
  winRate: number;
}

interface CompetitorShiftRow {
  name: string;
  prevAvgRank: number;
  currAvgRank: number;
  rankChange: number;
  prevAppearances: number;
  currAppearances: number;
}

interface NeighborhoodRow {
  area: string;
  avgRank: number;
  pointCount: number;
  totalCells: number;
  topKeyword: string;
  lat: number;
  lng: number;
}

interface ReportData {
  client: ClientInfo;
  periods: PeriodData[];
  keywords: KeywordRow[];
  reviews: ReviewRow[];
  wins: WinRow[];
  workCompleted: string[];
  tokenBalance: number;
  bestGrid: GridInfo | null;
  worstGrid: GridInfo | null;
  competitors: CompetitorRow[];
  competitorShift: CompetitorShiftRow[];
  neighborhoods: NeighborhoodRow[];
  mapCenter?: { lat: number; lng: number };
  jumpstart?: {
    gbpOptimized: boolean;
    photosUploaded: boolean;
    keywordsSet: boolean;
    citationAudit: boolean;
    postingActive: boolean;
    reviewStrategy: boolean;
    rankingVisible: boolean;
    directionsGrowing: boolean;
  };
  trafficTrends?: TrafficTrendPoint[];
  profileHealth?: {
    napConsistency: number;
    napDetail: string;
    photoCount: number;
    photoScore: number;
    reviewResponseRate: number;
    reviewResponseDetail: string;
    postFrequencyScore: number;
    postsLast30Days: number;
    profileCompleteness: number;
    totalCitations: number;
    syncedCitations: number;
    missingOpportunities: { title: string; desc: string }[];
  };
  predictions?: PredictionRow[];
  coldReads?: ColdReadRow[];
  peakEngagement?: PeakEngagementData;
  revealSequence?: RevealSequenceData;
  customerJourney?: CustomerJourneyData;
  timeMachine?: TimeMachineData;
  plantedSeeds?: PlantedSeedData[];
  personalTouch?: PersonalTouchData;
}

interface PeakEngagementData {
  heatmap: number[][];
  peakDay: string;
  peakHour: string;
  peakVal: number;
  searchImpressions: number;
  profileViews: number;
  totalActions: number;
  interactionBreakdown: {
    directions: number;
    websiteClicks: number;
    callActions: number;
  };
  directSearch: number;
  discoverySearch: number;
}

interface PredictionRow {
  keyword: string;
  currentRank: number;
  predictedRank: number;
  predictedBy: string;
  confidence: string;
  narrative: string;
  velocity: number;
}

interface ColdReadRow {
  id: string;
  type: string;
  message: string;
  severity: string;
}

interface RevealSequenceData {
  effort: {
    gbpRewrites: number;
    geoVariantsBuilt: number;
    keywordsTracked: number;
    competitorGridsMapped: number;
    postsPublished: number;
    reviewsResponded: number;
  };
  reach: {
    impressions: number;
    mapVisibility: number;
    competitorsDisplaced: number;
    rankingImprovements: number;
  };
  revenue: {
    calls: number;
    directions: number;
    avgTicketValue: number;
    potentialRevenue: number;
  };
}

interface CustomerJourneyData {
  neighborhood: string;
  keyword: string;
  rankPosition: number;
  address: string;
  totalReviews: number;
  avgRating: number;
  timestamp: string;
}

interface TimeMachineData {
  mapVisibility: { before: number; now: number };
  avgRankPosition: { before: number; now: number };
  competitorsDisplaced: { before: number; now: number };
  geoVariantServices: { before: number; now: number };
  monthlyCallClicks: { before: number; now: number };
}

interface PlantedSeedData {
  topic: string;
  keywords: string[];
  icon: string;
  competitorNote: string;
}

interface PersonalTouchData {
  clientFirstName: string;
  topCompetitorBeaten: string | null;
  competitorWinCount: number;
  topKeywordImproved: string | null;
  keywordImprovement: string | null;
  city: string;
  nextRecommendation: string;
}

interface ReportSettingsData {
  locationId: string;
  dateRangeType: string;
  startDaysAgo: number;
  endDaysAgo: number;
  compareWith: string;
  hideNegatives: boolean;
  scheduleEnabled: boolean;
  frequency: string;
  dayOfWeek: string;
  scheduleTime: string;
  emailEnabled: boolean;
  emailTo: string[];
  emailCc: string[];
  emailBcc: string[];
  smsEnabled: boolean;
  smsTo: string[];
  runImmediately: boolean;
}

interface ScoreResult {
  total: number;
  grade: string;
  gradeLabel: string;
  gradeColor: string;
  pillars: {
    visibility: { score: number; contribution: number };
    engagement: { score: number; contribution: number };
    reputation: { score: number; contribution: number };
    activity: { score: number; contribution: number };
  };
  biggestOpportunity: string;
}

function calcDominationScore(d: PeriodData): ScoreResult {
  const visMetric = Math.min((d.avgVisibility || 0) / 80, 1) * 100;
  const rankMetric = Math.max(0, (20 - (d.avgRank || 20)) / 17) * 100;
  const top3Metric = Math.min((d.keywordsInTop3 || 0) / 4, 1) * 100;
  const visibility = Math.min(Math.round(visMetric * 0.40 + rankMetric * 0.35 + top3Metric * 0.25), 100);

  const callsMetric = Math.min((d.calls || 0) / 20, 1) * 100;
  const dirsMetric = Math.min((d.directions || 0) / 100, 1) * 100;
  const clicksMetric = Math.min((d.clicks || 0) / 60, 1) * 100;
  const engagement = Math.min(Math.round(callsMetric * 0.50 + dirsMetric * 0.30 + clicksMetric * 0.20), 100);

  const ratMetric = (d.avgRating || 0) >= 4.5 ? 100 : (d.avgRating || 0) >= 4.0 ? 75 : (d.avgRating || 0) >= 3.5 ? 40 : (d.avgRating || 0) >= 3.0 ? 15 : 0;
  const countMetric = Math.min((d.totalReviews || 0) / 50, 1) * 100;
  const velocityMetric = Math.min((d.reviewsLast90Days || 0) / 6, 1) * 100;
  const responseMetric = Math.min((d.responseRate || 0) / 80, 1) * 100;
  const reputation = Math.min(Math.round(ratMetric * 0.35 + countMetric * 0.25 + velocityMetric * 0.25 + responseMetric * 0.15), 100);

  const postsMetric = Math.min((d.posts || 0) / 8, 1) * 100;
  const photosMetric = Math.min((d.photos || 0) / 5, 1) * 100;
  const profileMetric = Math.min((d.profileCompleteness || 0) / 100, 1) * 100;
  const activity = Math.min(Math.round(postsMetric * 0.45 + photosMetric * 0.20 + profileMetric * 0.35), 100);

  const total = Math.round(visibility * 0.30 + engagement * 0.25 + reputation * 0.25 + activity * 0.20);

  const grade = total >= 80 ? "A" : total >= 60 ? "B" : total >= 40 ? "C" : "D";
  const gradeLabel = total >= 80 ? "Dominating" : total >= 60 ? "Competing" : total >= 40 ? "Building" : "Starting";
  const gradeColor = total >= 80 ? "#10b981" : total >= 60 ? "#6366f1" : total >= 40 ? "#f59e0b" : "#ef4444";

  const subMetrics = [
    { name: "GeoGrid visibility", score: visMetric, action: "Run more geogrid scans and optimize for map pack" },
    { name: "Keyword rank", score: rankMetric, action: "Improve keyword rankings with targeted GBP posts" },
    { name: "Keywords in top 3", score: top3Metric, action: "Push more keywords into the local pack top 3" },
    { name: "Phone calls", score: callsMetric, action: "Add a strong call-to-action to your GBP profile" },
    { name: "Direction requests", score: dirsMetric, action: "Boost map visibility to drive more navigation requests" },
    { name: "Website clicks", score: clicksMetric, action: "Optimize your GBP website link and posts" },
    { name: "Star rating", score: ratMetric, action: "Maintain 4.5+ stars with consistent service quality" },
    { name: "Review count", score: countMetric, action: "Request reviews from recent customers" },
    { name: "Review velocity", score: velocityMetric, action: "Get 2+ new reviews per month consistently" },
    { name: "Response rate", score: responseMetric, action: "Reply to all reviews within 24 hours" },
    { name: "Post frequency", score: postsMetric, action: "Publish 8+ GBP posts per month" },
    { name: "Photo uploads", score: photosMetric, action: "Add 5+ new photos per month" },
    { name: "Profile completeness", score: profileMetric, action: "Fill in all GBP profile fields" },
  ];
  const lowest = subMetrics.reduce((min, m) => m.score < min.score ? m : min, subMetrics[0]);
  const biggestOpportunity = `${lowest.name} is at ${Math.round(lowest.score)}%. ${lowest.action}.`;

  return {
    total,
    grade,
    gradeLabel,
    gradeColor,
    pillars: {
      visibility: { score: visibility, contribution: Math.round(visibility * 0.30) },
      engagement: { score: engagement, contribution: Math.round(engagement * 0.25) },
      reputation: { score: reputation, contribution: Math.round(reputation * 0.25) },
      activity: { score: activity, contribution: Math.round(activity * 0.20) },
    },
    biggestOpportunity,
  };
}

function pctChange(curr: number, prev: number) {
  if (prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

function AnimatedNumber({ value, suffix = "", prefix = "" }: { value: number; suffix?: string; prefix?: string }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<number | null>(null);
  useEffect(() => {
    const step = (timestamp: number) => {
      if (ref.current === null) ref.current = timestamp;
      const progress = Math.min((timestamp - ref.current) / 1200, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    ref.current = null;
    requestAnimationFrame(step);
  }, [value]);
  return <>{prefix}{display}{suffix}</>;
}

function DominationScoreRing({ score, prevScore }: { score: ScoreResult; prevScore: ScoreResult }) {
  const delta = score.total - prevScore.total;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score.total / 100) * circumference;

  const pillarEntries: { label: string; key: keyof typeof score.pillars; weight: number; icon: string }[] = [
    { label: "Visibility", key: "visibility", weight: 30, icon: "📍" },
    { label: "Engagement", key: "engagement", weight: 25, icon: "📞" },
    { label: "Reputation", key: "reputation", weight: 25, icon: "⭐" },
    { label: "Activity", key: "activity", weight: 20, icon: "✍️" },
  ];

  return (
    <div data-testid="domination-score-ring">
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-7 mb-5">
        <div className="relative w-[130px] h-[130px] shrink-0">
          <svg width="130" height="130" viewBox="0 0 130 130">
            <defs>
              <filter id="scoreGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            <circle cx="65" cy="65" r="54" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
            <circle cx="65" cy="65" r="54" fill="none" stroke={score.gradeColor} strokeWidth="8"
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
              transform="rotate(-90 65 65)" filter="url(#scoreGlow)"
              style={{ transition: "stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[38px] font-extrabold text-white leading-none font-['Outfit',sans-serif]">
              <AnimatedNumber value={score.total} />
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] mt-0.5 font-semibold" style={{ color: score.gradeColor }}>{score.gradeLabel}</span>
          </div>
        </div>
        <div>
          <div className="text-xl font-bold text-white font-['Outfit',sans-serif] mb-1">Domination Score™</div>
          <div className="text-[13px] text-white/50 leading-relaxed mb-1.5">Visibility, Engagement, Reputation & Activity</div>
          {delta !== 0 && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-semibold ${delta > 0 ? "bg-emerald-500/[0.12] text-emerald-500" : "bg-red-500/[0.12] text-red-500"}`}>
              {delta > 0 ? "↑" : "↓"} {Math.abs(delta)} pts vs last period
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {pillarEntries.map(p => {
          const pillar = score.pillars[p.key];
          const prevPillar = prevScore.pillars[p.key];
          const pillarDelta = pillar.score - prevPillar.score;
          return (
            <div key={p.key} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]" data-testid={`pillar-${p.key}`}>
              <span className="text-sm">{p.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-white/50 font-medium">{p.label}</span>
                  <span className="text-[12px] font-bold text-white tabular-nums">{pillar.score}<span className="text-white/30 font-normal">/{p.weight === 30 ? "100" : "100"}</span></span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pillar.score}%`, background: `linear-gradient(to right, ${score.gradeColor}88, ${score.gradeColor})` }} />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-white/25">{pillar.contribution} of {p.weight} pts</span>
                  {pillarDelta !== 0 && (
                    <span className={`text-[10px] font-semibold ${pillarDelta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {pillarDelta > 0 ? "+" : ""}{pillarDelta}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {score.biggestOpportunity && (
        <div className="mt-3 px-3 py-2.5 rounded-lg bg-amber-500/[0.06] border border-amber-500/[0.12]" data-testid="biggest-opportunity">
          <div className="text-[10px] text-amber-400/60 font-semibold uppercase tracking-wider mb-0.5">Biggest Opportunity</div>
          <div className="text-[12px] text-amber-200/80 leading-relaxed">{score.biggestOpportunity}</div>
        </div>
      )}
    </div>
  );
}

function PredictionEngine({ predictions, coldReads }: { predictions: PredictionRow[]; coldReads: ColdReadRow[] }) {
  const confColors: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    high: { bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.2)", text: "#22c55e", icon: "🎯" },
    medium: { bg: "rgba(99,102,241,0.08)", border: "rgba(99,102,241,0.2)", text: "#6366f1", icon: "📈" },
    low: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.2)", text: "#f59e0b", icon: "📊" },
    warning: { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.2)", text: "#ef4444", icon: "⚠️" },
  };

  const hasPredictions = predictions && predictions.length > 0;
  const hasColdReads = coldReads && coldReads.length > 0;

  if (!hasPredictions && !hasColdReads) {
    return (
      <div className="report-card p-5 !bg-gradient-to-br !from-[#0e1628] !to-[#0d1520] !border-indigo-500/[0.15]" data-testid="prediction-engine">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">🔮</span>
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Prediction Engine</div>
        </div>
        <div className="text-sm text-white/40">Predictions will appear once we have enough GeoGrid scan history. Keep scanning to unlock keyword trajectory forecasts.</div>
      </div>
    );
  }

  return (
    <div className="report-card p-5 !bg-gradient-to-br !from-[#0e1628] !to-[#0d1520] !border-indigo-500/[0.15]" data-testid="prediction-engine">
      {hasPredictions && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🔮</span>
            <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Keyword Predictions</div>
          </div>
          <div className="space-y-2.5">
            {predictions.map((p, i) => {
              const style = confColors[p.confidence] || confColors.medium;
              return (
                <div
                  key={p.keyword}
                  className="rounded-lg p-3 animate-[fadeSlideUp_0.4s_both_ease]"
                  style={{ background: style.bg, border: `1px solid ${style.border}`, animationDelay: `${i * 150}ms` }}
                  data-testid={`prediction-${i}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{style.icon}</span>
                      <span className="text-[13px] font-semibold text-white font-['Outfit',sans-serif]">"{p.keyword}"</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.text, border: `1px solid ${style.border}` }}>
                        {p.confidence === "warning" ? "AT RISK" : `${p.confidence.toUpperCase()} CONF.`}
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-white/60 leading-relaxed">{p.narrative}</div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <div className="text-[10px] text-white/30">
                      Now: <span className="text-white/70 font-semibold">#{p.currentRank}</span>
                    </div>
                    <div className="text-[10px] text-white/30">→</div>
                    <div className="text-[10px] text-white/30">
                      {p.confidence === "warning" ? "Risk" : "Target"}: <span className="font-semibold" style={{ color: style.text }}>#{p.predictedRank}</span>
                    </div>
                    <div className="text-[10px] text-white/30">
                      By: <span className="text-white/70 font-semibold">{new Date(p.predictedBy).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    </div>
                    <div className="text-[10px] text-white/30">
                      Velocity: <span className="font-semibold" style={{ color: p.velocity > 0 ? "#22c55e" : "#ef4444" }}>{p.velocity > 0 ? "+" : ""}{p.velocity}/wk</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasColdReads && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">👁</span>
            <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Things We Noticed</div>
          </div>
          <div className="space-y-2">
            {coldReads.map((cr, i) => (
              <div
                key={cr.id}
                className="rounded-lg p-3 animate-[fadeSlideUp_0.4s_both_ease]"
                style={{
                  background: cr.severity === "warning" ? "rgba(245,158,11,0.06)" : "rgba(99,102,241,0.06)",
                  border: `1px solid ${cr.severity === "warning" ? "rgba(245,158,11,0.15)" : "rgba(99,102,241,0.15)"}`,
                  animationDelay: `${(predictions?.length || 0) * 150 + i * 150}ms`,
                }}
                data-testid={`cold-read-${i}`}
              >
                <div className="text-[11px] text-white/60 leading-relaxed">{cr.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, suffix, prevValue, icon, delay = 0, hideNeg = false }: {
  label: string; value: number; suffix?: string; prevValue?: number; icon: string; delay?: number; hideNeg?: boolean;
}) {
  const delta = prevValue !== undefined ? pctChange(value, prevValue) : null;
  const showDelta = delta !== null && delta !== 0 && !(hideNeg && delta < 0);
  return (
    <div className="report-card p-[18px_16px] relative overflow-hidden animate-[fadeSlideUp_0.5s_both_ease]"
      style={{ animationDelay: `${delay}ms` }} data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[22px] mb-2 opacity-80">{icon}</div>
      <div className="text-[28px] font-extrabold text-white font-['Outfit',sans-serif] leading-none">
        <AnimatedNumber value={value} suffix={suffix || ""} />
      </div>
      <div className="text-[11px] text-white/40 mt-1.5 font-medium">{label}</div>
      {showDelta && (
        <div className={`absolute top-3.5 right-3.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${delta! > 0 ? "bg-emerald-500/[0.12] text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
          {delta! > 0 ? "+" : ""}{delta}%
        </div>
      )}
    </div>
  );
}

function MiniGrid({ gridData, label, keyword, visibility, prevVisibility, hideNeg, points, centerLat, centerLng, mapsLoaded }: {
  gridData: number[][]; label: string; keyword: string; visibility: number; prevVisibility: number; hideNeg: boolean;
  points?: GridPointInfo[]; centerLat?: number; centerLng?: number; mapsLoaded?: boolean;
}) {
  const delta = visibility - prevVisibility;
  const showDelta = delta !== 0 && !(hideNeg && delta < 0);
  const getRankColor = (rank: number) => {
    if (rank === 1) return "#166534";
    if (rank <= 3) return "#22c55e";
    if (rank <= 10) return "#eab308";
    if (rank <= 20) return "#f97316";
    return "#dc2626";
  };
  const gridSize = gridData.length;
  const cellSize = gridSize <= 5 ? 28 : 20;
  const hasMapData = mapsLoaded && points && points.length > 0 && centerLat && centerLng;

  const darkMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#0d1117" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8b949e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#0d1117" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a1f2e" }] },
    { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a0e1a" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#1a1f2e" }] },
  ];

  return (
    <div className="report-card p-5 relative overflow-hidden" data-testid={`grid-card-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3 relative z-10">
        <div>
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">{label} Grid</div>
          <div className="text-sm font-semibold text-white mt-1 font-['Outfit',sans-serif]">"{keyword}"</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold text-white font-['Outfit',sans-serif]">{visibility}%</div>
          {showDelta && (
            <div className={`text-[11px] font-bold ${delta > 0 ? "text-emerald-500" : "text-red-500"}`}>
              {delta > 0 ? "↑" : "↓"} {Math.abs(Math.round(delta))}%
            </div>
          )}
        </div>
      </div>
      <div className="relative">
        {hasMapData && (
          <div className="absolute inset-0 rounded-xl overflow-hidden opacity-30" style={{ margin: "-8px" }}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={{ lat: centerLat!, lng: centerLng! }}
              zoom={11}
              options={{
                disableDefaultUI: true,
                draggable: false,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                clickableIcons: false,
                styles: darkMapStyles,
                gestureHandling: "none",
              }}
            >
              {points!.map((pt, i) => (
                <OverlayViewF key={i} position={{ lat: pt.lat, lng: pt.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: getRankColor(pt.rank),
                    border: "1.5px solid rgba(255,255,255,0.3)",
                    transform: "translate(-50%, -50%)",
                    opacity: 0.9,
                  }} />
                </OverlayViewF>
              ))}
            </GoogleMap>
          </div>
        )}
        <div className="flex justify-center relative z-10">
          <div className="inline-grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${gridSize}, ${cellSize}px)` }}>
            {gridData.flat().map((rank, i) => (
              <div key={i} className="flex items-center justify-center rounded text-[10px] font-bold text-white"
                style={{ width: cellSize, height: cellSize, backgroundColor: rank > 0 ? getRankColor(rank) : "rgba(255,255,255,0.05)" }}>
                {rank > 0 ? rank : "–"}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WinsFeed({ wins }: { wins: WinRow[] }) {
  if (wins.length === 0) return null;
  return (
    <div className="flex flex-col gap-2.5" data-testid="wins-feed">
      {wins.map((w, i) => (
        <div key={i} className="relative px-4 py-3 rounded-xl border border-orange-500/20 bg-gradient-to-br from-orange-500/[0.06] to-orange-600/[0.02] animate-[fadeSlideUp_0.4s_both_ease] overflow-hidden group hover:border-orange-500/30 transition-all"
          style={{ animationDelay: `${i * 80}ms`, boxShadow: "0 0 20px rgba(232,114,42,0.04)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-500/[0.12] flex items-center justify-center text-base shrink-0">{w.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-slate-200 leading-snug truncate">{w.text}</div>
              <div className="text-[10px] text-white/30 mt-0.5">{w.time}</div>
            </div>
            <span className="text-[10px] font-bold text-orange-400 px-2 py-0.5 rounded-full bg-orange-500/[0.12] border border-orange-500/20 whitespace-nowrap shrink-0">{w.delta}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExecutiveSummary({ data, prevData, score, prevScore }: { data: PeriodData; prevData: PeriodData; score: ScoreResult; prevScore: ScoreResult }) {
  const insights: string[] = [];
  const visChange = score.pillars.visibility.score - prevScore.pillars.visibility.score;
  if (visChange > 0) insights.push(`Google Maps visibility increased by ${visChange} points, driven by optimized local listings.`);
  else if (visChange < 0) insights.push(`Google Maps visibility dropped by ${Math.abs(visChange)} points. Focus on GBP optimization.`);

  const callsDelta = (data.calls || 0) - (prevData.calls || 0);
  const dirDelta = (data.directions || 0) - (prevData.directions || 0);
  if (callsDelta > 0 && data.calls > 0) insights.push(`Direct calls increased by ${callsDelta} (${prevData.calls > 0 ? `+${Math.round((callsDelta / prevData.calls) * 100)}%` : "new activity"}) — more customers are reaching out.`);
  if (dirDelta > 0 && data.directions > 0) insights.push(`Direction requests grew by ${dirDelta} — your local visibility is converting to foot traffic.`);

  const viewsDelta = (data.views || 0) - (prevData.views || 0);
  if (viewsDelta > 0 && data.views > 0) insights.push(`Profile views reached ${data.views.toLocaleString()}${prevData.views > 0 ? `, up ${Math.round((viewsDelta / prevData.views) * 100)}% from the previous period` : " — new visibility gained"}.`);

  const ratingDelta = (data.avgRating || 0) - (prevData.avgRating || 0);
  if (ratingDelta > 0) insights.push(`Review rating improved to ${data.avgRating?.toFixed(1)} stars, boosting local trust.`);
  if ((data.totalReviews || 0) > (prevData.totalReviews || 0)) insights.push(`Review velocity improved with ${(data.totalReviews || 0) - (prevData.totalReviews || 0)} new reviews this period.`);

  if (data.keywordsInTop3 > 0) insights.push(`${data.keywordsInTop3} keyword${data.keywordsInTop3 > 1 ? "s" : ""} ranking in the top 3 of the local map pack.`);
  if (insights.length === 0) insights.push(`Domination Score stands at ${score.total}/100. ${score.total >= 60 ? "Strong foundation in place." : "Room for growth across key pillars."}`);
  const displayInsights = insights.slice(0, 5);

  const recommendations: string[] = [];
  if (score.pillars.reputation.score < 50) recommendations.push("Implement a proactive review generation strategy for consistent feedback.");
  if (score.pillars.activity.score < 50) recommendations.push("Increase posting frequency to 8+ GBP posts per month.");
  if (score.pillars.visibility.score < 50) recommendations.push("Prioritize citation building to boost local authority.");
  if (score.pillars.engagement.score < 50) recommendations.push("Optimize your GBP profile CTA to drive more calls.");
  if (recommendations.length === 0) recommendations.push("Continue current strategy and monitor for competitor movements.");

  const yoyData = [
    { label: "Clicks to Call", current: data.calls, previous: prevData.calls, color: "#3b82f6" },
    { label: "Website Visits", current: data.clicks, previous: prevData.clicks, color: "#10b981" },
    { label: "Direction Requests", current: data.directions, previous: prevData.directions, color: "#8b5cf6" },
  ];

  const allFocusItems = [
    { title: "Citation Building", desc: `Register on 15+ new high-authority local directories.`, icon: "📋", pillar: "visibility" as const, threshold: 60 },
    { title: "Review Generation", desc: `Launch automated email campaigns for customer feedback.`, icon: "⭐", pillar: "reputation" as const, threshold: 60 },
    { title: "Content Expansion", desc: `Create 5 new service-area pages with localized keywords.`, icon: "📝", pillar: "activity" as const, threshold: 60 },
    { title: "GBP Profile Optimization", desc: `Complete all missing profile fields and update business description.`, icon: "🎯", pillar: "visibility" as const, threshold: 80 },
    { title: "Engagement Boost", desc: `Add click-to-call buttons and optimize your profile CTA for conversions.`, icon: "📞", pillar: "engagement" as const, threshold: 60 },
    { title: "Photo & Media Push", desc: `Upload 10+ high-quality photos of your business, team, and services.`, icon: "📸", pillar: "activity" as const, threshold: 80 },
  ];
  const underperforming = allFocusItems
    .filter(item => (score.pillars[item.pillar]?.score || 0) < item.threshold)
    .sort((a, b) => (score.pillars[a.pillar]?.score || 0) - (score.pillars[b.pillar]?.score || 0));
  const focusItems = underperforming.length > 0 ? underperforming.slice(0, 3) : allFocusItems.slice(0, 3);

  return (
    <div className="report-card p-6" data-testid="executive-summary">
      <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase mb-4">Executive Summary & AI Insights</div>

      <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4 mb-5">
        <div className="text-[13px] font-bold text-white mb-2 font-['Outfit',sans-serif]">AI-Generated Summary</div>
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] text-white/50 font-semibold mb-1">Key Takeaways:</div>
          {displayInsights.map((insight, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px] text-white/60 leading-relaxed">
              <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
              <span>{insight}</span>
            </div>
          ))}
          <div className="text-[11px] text-white/50 font-semibold mt-2 mb-1">Automated Recommendations:</div>
          {recommendations.map((rec, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px] text-white/60 leading-relaxed">
              <span className="text-indigo-400 mt-0.5 shrink-0">•</span>
              <span>{rec}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[13px] font-bold text-white mb-3 font-['Outfit',sans-serif]">Year-over-Year Growth Comparison</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {yoyData.map((item) => {
          const pctDelta = item.previous > 0 ? Math.round(((item.current - item.previous) / item.previous) * 100) : 0;
          const maxVal = Math.max(item.current, item.previous, 1);
          const prevH = Math.max(12, (item.previous / maxVal) * 80);
          const currH = Math.max(12, (item.current / maxVal) * 80);
          return (
            <div key={item.label} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4" data-testid={`yoy-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[12px] text-white/60 font-semibold">{item.label}</div>
                {pctDelta !== 0 && (
                  <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pctDelta > 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                    {pctDelta > 0 ? "+" : ""}{pctDelta}%
                  </div>
                )}
              </div>
              <div className="flex items-end justify-center gap-5 h-[100px] mb-2">
                <div className="flex flex-col items-center gap-1">
                  <div className="text-[11px] text-white/50 font-semibold">{item.previous.toLocaleString()}</div>
                  <div className="w-12 rounded-t-md bg-white/[0.08]" style={{ height: `${prevH}px` }} />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="text-[11px] text-white font-bold">{item.current.toLocaleString()}</div>
                  <div className="w-12 rounded-t-md" style={{ height: `${currH}px`, backgroundColor: item.color }} />
                </div>
              </div>
              <div className="flex items-center justify-center gap-6 pt-2 border-t border-white/[0.04]">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-white/[0.08]" /><span className="text-[10px] text-white/40">Previous</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} /><span className="text-[10px] text-white/40">Current</span></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-bold text-white font-['Outfit',sans-serif]">Next Month's Focus</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {focusItems.map((item) => (
          <div key={item.title} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3.5 hover:border-white/[0.1] transition-colors" data-testid={`focus-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xl mb-2">{item.icon}</div>
            <div className="text-[12px] font-bold text-white mb-1">{item.title}</div>
            <div className="text-[11px] text-white/40 leading-relaxed"><span className="text-white/50 font-semibold">Action:</span> {item.desc}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-5">
        <button onClick={() => { trackEvent('report_downloaded', { report_type: 'full' }); window.print(); }} className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[12px] font-semibold transition-colors shadow-[0_0_15px_rgba(16,185,129,0.2)]" data-testid="download-full-report">
          Download Full Report
        </button>
      </div>
    </div>
  );
}

function ProfileHealthAudit({ data, profileHealth }: { data: PeriodData; profileHealth?: ReportData["profileHealth"] }) {
  const ph = profileHealth;
  const napScore = ph ? ph.napConsistency : Math.min(Math.round((data.profileCompleteness || 0) * 0.92), 100);
  const photoScore = ph ? ph.photoScore : Math.min(Math.round(((data.photos || 0) / 10) * 100), 100);
  const reviewResponseScore = ph ? ph.reviewResponseRate : Math.min(Math.round(data.responseRate || 0), 100);
  const postFreqScore = ph ? ph.postFrequencyScore : Math.min(Math.round(((data.posts || 0) / 8) * 100), 100);
  const profileComplete = ph ? Math.min(ph.profileCompleteness, 100) : Math.min(data.profileCompleteness || 0, 100);

  const napDetail = ph ? ph.napDetail : (napScore >= 80 ? "Verified across 25+ directories." : "Some directory discrepancies found.");
  const photoDetail = ph ? `${ph.photoCount} photos uploaded` : (photoScore >= 80 ? "High-resolution images. Good variety." : "Need more high-resolution interior shots.");
  const reviewDetail = ph ? ph.reviewResponseDetail : (reviewResponseScore >= 80 ? "All reviews responded to within 24 hours." : "Some reviews need responses.");
  const postDetail = ph ? `${ph.postsLast30Days} posts in last 30 days` : (postFreqScore >= 80 ? "Consistent weekly updates. Great engagement." : "Consider posting more frequently.");

  const healthGauges = [
    { label: "NAP Consistency", score: napScore, detail: napDetail, status: napScore >= 80 ? "good" as const : "warn" as const },
    { label: "Photo Quality", score: photoScore, detail: photoDetail, status: photoScore >= 80 ? "good" as const : "warn" as const },
    { label: "Review Response Rate", score: reviewResponseScore, detail: reviewDetail, status: reviewResponseScore >= 80 ? "good" as const : "warn" as const },
    { label: "Post Frequency", score: postFreqScore, detail: postDetail, status: postFreqScore >= 80 ? "good" as const : "warn" as const },
  ];

  const opportunities = ph && ph.missingOpportunities.length > 0
    ? ph.missingOpportunities
    : [
        { title: "Add Q&A section", desc: "Answer common customer questions to improve engagement." },
        { title: "Upload video", desc: "Showcase your business environment or services with a video." },
      ];

  const gaugeCircle = (score: number, size: number = 80) => {
    const r = (size - 8) / 2;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)" }} />
        <text x={size/2} y={size/2 - 4} textAnchor="middle" fill="white" fontSize="18" fontWeight="800" fontFamily="Outfit">{score}%</text>
        <text x={size/2} y={size/2 + 12} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8" fontWeight="500"></text>
      </svg>
    );
  };

  return (
    <div className="report-card p-6" data-testid="profile-health-audit">
      <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase mb-5">Profile Health & Audit Details</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="text-[13px] font-bold text-white mb-4 font-['Outfit',sans-serif]">Business Profile Health</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {healthGauges.map((g) => (
              <div key={g.label} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col items-center text-center" data-testid={`health-gauge-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {gaugeCircle(g.score)}
                <div className="text-[11px] font-semibold text-white/70 mt-1">{g.label}</div>
                <div className="text-[10px] text-white/35 mt-1 leading-relaxed">{g.detail}</div>
                <div className={`text-[10px] mt-1.5 ${g.status === "good" ? "text-emerald-400" : "text-amber-400"}`}>
                  {g.status === "good" ? "✓ " : "⚠ "}{g.status === "good" ? "Healthy" : "Needs attention"}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[13px] font-bold text-white mb-4 font-['Outfit',sans-serif]">Profile Completeness</div>
          <div className="flex justify-center mb-4">
            {gaugeCircle(profileComplete, 120)}
          </div>
          <div className="text-center text-[11px] text-white/40 mb-5">Profile Completeness Score</div>

          <div className="text-[13px] font-bold text-white mb-3 font-['Outfit',sans-serif]">Missing Opportunities</div>
          <div className="flex flex-col gap-2">
            {opportunities.map((opp, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid={`opportunity-${i}`}>
                <div className="w-7 h-7 rounded-lg bg-indigo-500/[0.1] flex items-center justify-center shrink-0">
                  <Plus className="w-3.5 h-3.5 text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-white">{opp.title}</div>
                  <div className="text-[11px] text-white/35 leading-relaxed">{opp.desc}</div>
                </div>
                <button className="text-[10px] font-semibold text-indigo-400 px-2.5 py-1 rounded-md border border-indigo-500/20 bg-indigo-500/[0.06] hover:bg-indigo-500/[0.12] transition-colors whitespace-nowrap shrink-0">
                  Take Action
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KeywordTable({ keywords, hideNeg }: { keywords: KeywordRow[]; hideNeg: boolean }) {
  if (keywords.length === 0) return <div className="text-sm text-white/30">No keywords tracked yet</div>;
  return (
    <div className="flex flex-col gap-1.5" data-testid="keyword-table">
      {keywords.map((kw, i) => {
        const delta = Math.round((kw.prev - kw.rank) * 10) / 10;
        const improved = kw.rank < kw.prev;
        const showDelta = delta !== 0 && !(hideNeg && !improved);
        return (
          <div key={i} className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[13px] font-extrabold font-['Outfit',sans-serif] shrink-0 ${
              kw.visibility >= 60 ? "bg-emerald-500/[0.12] text-emerald-500" :
              kw.visibility >= 30 ? "bg-amber-500/[0.12] text-amber-500" : "bg-red-500/[0.08] text-red-500"
            }`}>{Math.round(kw.rank)}</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-slate-200">{kw.name}</div>
              <div className="text-[11px] text-white/30">{kw.visibility}% visible</div>
            </div>
            {showDelta && (
              <span className={`text-[11px] font-bold ${improved ? "text-emerald-500" : "text-red-500"}`}>
                {improved ? "↑" : "↓"} {Math.abs(delta)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Roadmap({ currentStage }: { currentStage: number }) {
  const stages = [
    { num: 1, title: "Visibility Fix" },
    { num: 2, title: "Lead Generation" },
    { num: 3, title: "Conversion Optimization" },
    { num: 4, title: "AI Domination" },
    { num: 5, title: "Market Takeover" },
  ];
  return (
    <div data-testid="roadmap" className="overflow-x-auto">
      <div className="relative flex items-center justify-between px-2 py-4 min-w-[400px]">
        <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-0.5 bg-gradient-to-r from-indigo-500/40 via-indigo-500/20 to-white/[0.06]" />
        {stages.map((s) => {
          const isActive = s.num === currentStage;
          const isComplete = s.num < currentStage;
          const isFuture = s.num > currentStage;
          return (
            <div key={s.num} className="flex flex-col items-center gap-1.5 relative z-10">
              <div className="text-[9px] text-white/30 font-semibold uppercase tracking-wider">Step {s.num}</div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-extrabold font-['Outfit',sans-serif] shrink-0 transition-all duration-300 ${
                isComplete ? "bg-emerald-500 text-white ring-2 ring-emerald-500/30" :
                isActive ? "bg-gradient-to-br from-orange-500 to-orange-600 text-white ring-2 ring-orange-500/30 shadow-[0_0_15px_rgba(232,114,42,0.3)]" :
                "bg-white/5 text-white/25 ring-1 ring-white/[0.06]"
              }`}>{isComplete ? "✓" : s.num}</div>
              <div className={`text-[9px] text-center leading-tight max-w-[70px] font-medium ${isFuture ? "text-white/25" : isActive ? "text-orange-400" : "text-white/60"}`}>
                {s.title}
              </div>
              {isActive && <div className="text-[8px] text-orange-400/70 font-semibold">"You are here"</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: ReviewRow }) {
  return (
    <div className="p-3.5 rounded-[10px] bg-white/[0.02] border border-white/5 hover:border-orange-500/20 transition-colors" data-testid={`review-card-${review.author}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-[14px] font-bold text-white shrink-0 shadow-[0_0_10px_rgba(232,114,42,0.2)]">{(review.author || "A")[0]}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[13px] font-semibold text-slate-200">{review.author}</div>
          </div>
          <div className="text-[13px] text-amber-400 mb-1.5">{"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}</div>
          <div className="text-xs text-white/50 leading-relaxed">{review.snippet}</div>
        </div>
      </div>
    </div>
  );
}

function ThirtyDayTracker({ data, jumpstart, isPublic }: { data: PeriodData; jumpstart?: ReportData["jumpstart"]; isPublic?: boolean }) {
  const js = jumpstart || {
    gbpOptimized: false,
    photosUploaded: data.photos > 0,
    keywordsSet: data.keywords > 0,
    citationAudit: false,
    postingActive: data.posts >= 3,
    reviewStrategy: data.responseRate > 20,
    rankingVisible: data.avgVisibility > 25,
    directionsGrowing: data.directions > 30,
  };
  const items = [
    { task: "GBP profile optimized", done: js.gbpOptimized, link: "/gbp-optimization", hint: "Optimize your Google Business Profile" },
    { task: "Photos & media uploaded", done: js.photosUploaded, link: "/listing", hint: "Add photos to your listing" },
    { task: "Initial keyword targets set", done: js.keywordsSet, link: "/geogrid", hint: "Set up keyword tracking" },
    { task: "Citation audit complete", done: js.citationAudit, link: "/citations", hint: "Run a citation audit" },
    { task: "Weekly posting cadence active", done: js.postingActive, link: "/posts", hint: "Schedule weekly posts" },
    { task: "Review response strategy live", done: js.reviewStrategy, link: "/reviews", hint: "Respond to your reviews" },
    { task: "First ranking improvements visible", done: js.rankingVisible, link: "/geogrid", hint: "Run a GeoGrid scan" },
    { task: "Direction requests increasing", done: js.directionsGrowing, link: "/dashboard", hint: "View your performance metrics" },
  ];
  const completed = items.filter(i => i.done).length;
  const pct = Math.round((completed / items.length) * 100);
  return (
    <div data-testid="thirty-day-tracker">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-bold text-white font-['Outfit',sans-serif]">30-Day Jumpstart</span>
        <span className="text-[13px] font-bold text-emerald-500">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] mb-3.5 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${pct}%`, transitionDuration: "1200ms" }} />
      </div>
      <div className="flex flex-col gap-1.5">
        {items.map((item, i) => {
          const showLink = !item.done && !isPublic;
          const content = (
            <div key={i} className={`flex items-center gap-2.5 group ${showLink ? "cursor-pointer hover:opacity-70 transition-opacity" : ""} ${item.done ? "opacity-100" : "opacity-60"}`}
              data-testid={`jumpstart-item-${i}`}>
              <div className={`w-5 h-5 rounded-[5px] flex items-center justify-center text-[11px] shrink-0 ${
                item.done ? "bg-emerald-500/[0.15] text-emerald-500" : "bg-white/[0.04] text-white/20 border border-white/[0.08]"
              }`}>{item.done ? "✓" : ""}</div>
              <div className="flex-1 min-w-0">
                <span className={`text-xs ${item.done ? "text-slate-200" : "text-white/50"}`}>{item.task}</span>
                {showLink && (
                  <div className="text-[10px] text-indigo-400 mt-0.5 flex items-center gap-1">
                    <span>{item.hint}</span>
                    <span className="group-hover:translate-x-0.5 transition-transform">→</span>
                  </div>
                )}
              </div>
            </div>
          );
          if (showLink) {
            return <Link key={i} href={item.link} className="no-underline">{content}</Link>;
          }
          return content;
        })}
      </div>
    </div>
  );
}

function WorkLog({ items }: { items: string[] }) {
  if (items.length === 0) return <div className="text-sm text-white/30">No completed tasks yet</div>;
  return (
    <div className="flex flex-col gap-1.5" data-testid="work-log">
      {items.map((item, i) => (
        <div key={i} className={`flex items-start gap-2.5 py-2 ${i < items.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
          <span className="text-indigo-500 text-sm mt-px shrink-0">◆</span>
          <span className="text-[13px] text-white/60 leading-snug">{item}</span>
        </div>
      ))}
    </div>
  );
}

function PeakEngagementAnalysis({ engagement, data, prevData }: { engagement: PeakEngagementData; data: PeriodData; prevData: PeriodData }) {
  const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const hours = ["6am", "9am", "12pm", "3pm", "6pm", "9pm"];

  const maxVal = useMemo(() => {
    let mx = 0;
    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 6; h++) {
        if ((engagement.heatmap[d]?.[h] || 0) > mx) mx = engagement.heatmap[d]?.[h] || 0;
      }
    }
    return mx;
  }, [engagement.heatmap]);

  const getColor = (val: number) => {
    if (maxVal === 0) return "rgba(59,130,246,0.08)";
    const ratio = val / maxVal;
    if (ratio > 0.75) return "rgba(200,120,50,0.85)";
    if (ratio > 0.55) return "rgba(180,100,40,0.7)";
    if (ratio > 0.4) return "rgba(80,130,200,0.7)";
    if (ratio > 0.25) return "rgba(70,110,180,0.55)";
    if (ratio > 0.1) return "rgba(60,100,170,0.4)";
    return "rgba(40,60,100,0.3)";
  };

  const prevViews = prevData.views || 0;
  const viewsPctChange = prevViews > 0 ? Math.round(((data.views - prevViews) / prevViews) * 1000) / 10 : 0;
  const prevImpressions = prevData.searchImpressions + prevData.mapsImpressions || 0;
  const curImpressions = engagement.searchImpressions;
  const impressionsPctChange = prevImpressions > 0 ? Math.round(((curImpressions - prevImpressions) / prevImpressions) * 1000) / 10 : 0;

  return (
    <div className="report-card p-6" data-testid="peak-engagement-analysis">
      <div className="mb-1">
        <div className="text-[18px] font-bold text-white font-['Outfit',sans-serif]">Peak Engagement Analysis</div>
        <div className="text-[12px] text-white/40 mt-0.5">Visualizing customer interaction patterns across Google Maps and Business Profile platforms</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 mt-5">
        <div className="rounded-xl p-5" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[14px] font-semibold text-[#e8722a] font-['Outfit',sans-serif]">Interactions Heatmap</div>
              <div className="text-[10px] text-white/35 mt-0.5">User activities synced from Google Business API</div>
            </div>
            <div className="rounded-lg px-3 py-1.5" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <div className="text-[9px] text-white/40 uppercase tracking-wider">Most Active</div>
              <div className="text-[12px] font-semibold text-white">
                <span className="text-[#e8722a]">{engagement.peakDay} at {engagement.peakHour}</span>
                <span className="text-white/40"> ({engagement.peakVal} ints)</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[3px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-10 shrink-0" />
              <div className="flex gap-[3px] flex-1">
                {days.map(day => (
                  <div key={day} className="flex-1 text-[10px] text-white/50 text-center font-semibold">{day}</div>
                ))}
              </div>
            </div>
            {hours.map((hour, h) => (
              <div key={hour} className="flex items-center gap-2">
                <span className="text-[10px] text-white/40 w-10 text-right shrink-0">{hour}</span>
                <div className="flex gap-[3px] flex-1">
                  {days.map((day, d) => {
                    const val = engagement.heatmap[d]?.[h] || 0;
                    return (
                      <div
                        key={d}
                        className="flex-1 h-10 rounded-md flex items-center justify-center transition-colors cursor-default"
                        style={{ backgroundColor: getColor(val) }}
                        title={`${day} ${hour}: ${val} interactions`}
                      >
                        {val > 0 && maxVal > 0 && val / maxVal > 0.6 && (
                          <span className="text-[10px] font-semibold text-white/80">{val}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="text-[10px] text-white/30 uppercase tracking-wider">Low Engagement</span>
            <div className="flex gap-0.5">
              {[
                "rgba(40,60,100,0.3)",
                "rgba(60,100,170,0.4)",
                "rgba(70,110,180,0.55)",
                "rgba(80,130,200,0.7)",
                "rgba(180,100,40,0.7)",
                "rgba(200,120,50,0.85)",
              ].map((c, i) => (
                <div key={i} className="w-6 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span className="text-[10px] text-white/30 uppercase tracking-wider">High Dominance</span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-xl p-4" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Search Impressions</div>
              <div className="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </div>
            </div>
            <div className="text-[28px] font-bold text-white font-['Outfit',sans-serif] leading-none">{engagement.searchImpressions.toLocaleString()}</div>
            {impressionsPctChange !== 0 && (
              <div className="flex items-center gap-1 mt-1.5">
                <span className={`text-[11px] font-semibold ${impressionsPctChange > 0 ? "text-[#e8722a]" : "text-red-400"}`}>
                  {impressionsPctChange > 0 ? "↗" : "↘"}{impressionsPctChange > 0 ? "+" : ""}{impressionsPctChange}%
                </span>
                <span className="text-[10px] text-white/30">vs last month</span>
              </div>
            )}
            <div className="h-1 rounded-full bg-blue-500/20 mt-3">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${Math.min(100, Math.max(10, (impressionsPctChange + 50)))}%` }} />
            </div>
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Profile Views</div>
              <div className="w-7 h-7 rounded-full bg-[#e8722a]/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full border-2 border-[#e8722a]" />
              </div>
            </div>
            <div className="text-[28px] font-bold text-white font-['Outfit',sans-serif] leading-none">{engagement.profileViews.toLocaleString()}</div>
            {viewsPctChange !== 0 && (
              <div className="flex items-center gap-1 mt-1.5">
                <span className={`text-[11px] font-semibold ${viewsPctChange > 0 ? "text-[#e8722a]" : "text-red-400"}`}>
                  {viewsPctChange > 0 ? "↗" : "↘"}{viewsPctChange > 0 ? "+" : ""}{viewsPctChange}%
                </span>
                <span className="text-[10px] text-white/30">vs last month</span>
              </div>
            )}
            <div className="h-1 rounded-full bg-[#e8722a]/20 mt-3">
              <div className="h-full rounded-full bg-gradient-to-r from-[#e8722a] to-[#f59e0b]" style={{ width: `${Math.min(100, Math.max(10, (viewsPctChange + 50)))}%` }} />
            </div>
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="text-[12px] font-semibold text-white mb-3 font-['Outfit',sans-serif]">Interaction Breakdown</div>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-[12px] text-white/60">Directions</span>
                </div>
                <span className="text-[13px] font-bold text-white">{engagement.interactionBreakdown.directions}%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#e8722a]" />
                  <span className="text-[12px] text-white/60">Website Clicks</span>
                </div>
                <span className="text-[13px] font-bold text-white">{engagement.interactionBreakdown.websiteClicks}%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
                  <span className="text-[12px] text-white/60">Call Actions</span>
                </div>
                <span className="text-[13px] font-bold text-white">{engagement.interactionBreakdown.callActions}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <div className="rounded-xl p-3.5 text-center" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[9px] text-white/35 uppercase tracking-wider font-semibold mb-1">Total Actions</div>
          <div className="text-[22px] font-bold text-white font-['Outfit',sans-serif] leading-none">{engagement.totalActions.toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-3.5 text-center" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[9px] text-white/35 uppercase tracking-wider font-semibold mb-1">Avg Session</div>
          <div className="text-[22px] font-bold text-white font-['Outfit',sans-serif] leading-none">
            {Math.floor(engagement.totalActions / Math.max(1, engagement.profileViews) * 240)}s
          </div>
        </div>
        <div className="rounded-xl p-3.5 text-center" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[9px] text-white/35 uppercase tracking-wider font-semibold mb-1">Direct Search</div>
          <div className="text-[22px] font-bold text-white font-['Outfit',sans-serif] leading-none">{engagement.directSearch}%</div>
        </div>
        <div className="rounded-xl p-3.5 text-center" style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[9px] text-white/35 uppercase tracking-wider font-semibold mb-1">Discovery</div>
          <div className="text-[22px] font-bold text-white font-['Outfit',sans-serif] leading-none">{engagement.discoverySearch}%</div>
        </div>
      </div>
    </div>
  );
}

function TrafficTrendsChart({ data, prevData, trends }: { data: PeriodData; prevData: PeriodData; trends: TrafficTrendPoint[] }) {
  const chartData = trends.length > 0 ? trends : [
    { label: "Week 1", views: Math.round(data.views * 0.2), calls: Math.round(data.calls * 0.2), directions: Math.round(data.directions * 0.18), clicks: Math.round(data.clicks * 0.15), searchImpressions: Math.round(data.searchImpressions * 0.2), mapsImpressions: Math.round(data.mapsImpressions * 0.2) },
    { label: "Week 2", views: Math.round(data.views * 0.22), calls: Math.round(data.calls * 0.22), directions: Math.round(data.directions * 0.22), clicks: Math.round(data.clicks * 0.2), searchImpressions: Math.round(data.searchImpressions * 0.22), mapsImpressions: Math.round(data.mapsImpressions * 0.22) },
    { label: "Week 3", views: Math.round(data.views * 0.28), calls: Math.round(data.calls * 0.28), directions: Math.round(data.directions * 0.28), clicks: Math.round(data.clicks * 0.3), searchImpressions: Math.round(data.searchImpressions * 0.28), mapsImpressions: Math.round(data.mapsImpressions * 0.28) },
    { label: "Week 4", views: Math.round(data.views * 0.3), calls: Math.round(data.calls * 0.3), directions: Math.round(data.directions * 0.32), clicks: Math.round(data.clicks * 0.35), searchImpressions: Math.round(data.searchImpressions * 0.3), mapsImpressions: Math.round(data.mapsImpressions * 0.3) },
  ];

  const CustomTooltip = ({ active, payload, label: tooltipLabel }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="rounded-xl px-3.5 py-2.5 text-[12px] border border-white/10 shadow-2xl" style={{ background: "#0d1220" }}>
        <div className="text-white/50 text-[11px] font-semibold mb-1.5">{tooltipLabel}</div>
        {payload.map((p: any, i: number) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-white/70">{p.name}:</span>
            <span className="text-white font-semibold">{p.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="report-card p-6" data-testid="traffic-trends-chart">
      <div className="text-[15px] font-bold text-white font-['Outfit',sans-serif] mb-5">Traffic & Engagement Trends</div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4 lg:gap-5">
        <div className="flex flex-col gap-4">
          <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[12px] font-semibold text-white/60">Search vs. Map Impressions</div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#06b6d4]" /><span className="text-[10px] text-white/40">Search Impressions</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#8b5cf6]" /><span className="text-[10px] text-white/40">Map Impressions</span></div>
              </div>
            </div>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="searchGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="mapsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.06)" }} tickLine={false} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="searchImpressions" name="Search Impressions" stroke="#06b6d4" strokeWidth={2.5} fill="url(#searchGrad)" dot={false} activeDot={{ r: 4, fill: "#06b6d4", stroke: "#fff", strokeWidth: 1.5 }} />
                  <Area type="monotone" dataKey="mapsImpressions" name="Map Impressions" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#mapsGrad)" dot={false} activeDot={{ r: 4, fill: "#8b5cf6", stroke: "#fff", strokeWidth: 1.5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[12px] font-semibold text-white/60">Action Trends: Visits, Calls, Directions</div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#ec4899]" /><span className="text-[10px] text-white/40">Website Visits</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#10b981]" /><span className="text-[10px] text-white/40">Phone Calls</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#eab308]" /><span className="text-[10px] text-white/40">Direction Requests</span></div>
              </div>
            </div>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="clicksGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="callsGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="dirsGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.06)" }} tickLine={false} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="clicks" name="Website Visits" stroke="#ec4899" strokeWidth={2} fill="url(#clicksGrad2)" dot={false} activeDot={{ r: 3, fill: "#ec4899" }} />
                  <Area type="monotone" dataKey="calls" name="Phone Calls" stroke="#10b981" strokeWidth={2} fill="url(#callsGrad2)" dot={false} activeDot={{ r: 3, fill: "#10b981" }} />
                  <Area type="monotone" dataKey="directions" name="Direction Requests" stroke="#eab308" strokeWidth={2} fill="url(#dirsGrad2)" dot={false} activeDot={{ r: 3, fill: "#eab308" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function useScrollReveal(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function PredictionRevealSection({ predictions }: { predictions: PredictionRow[] }) {
  const { ref, visible } = useScrollReveal(0.3);
  if (!predictions || predictions.length === 0) return null;

  return (
    <div ref={ref} className="report-card p-6" data-testid="prediction-reveal">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">🔮</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">Prediction Revealed</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {predictions.slice(0, 2).map((p, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden" style={{ border: "1px solid rgba(0, 212, 170, 0.3)" }} data-testid={`prediction-reveal-${i}`}>
            <div
              className="absolute inset-0 z-[2] flex items-center justify-center transition-opacity duration-[1200ms]"
              style={{
                background: "rgba(15, 15, 25, 0.85)",
                backdropFilter: "blur(8px)",
                opacity: visible ? 0 : 1,
                pointerEvents: visible ? "none" : "auto",
                transitionDelay: `${600 + i * 400}ms`,
              }}
            >
              <div className="text-white/40 text-sm font-semibold flex items-center gap-2">
                <span>🔒</span> Sealed Prediction
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#00D4AA]/10 text-[#00D4AA] border border-[#00D4AA]/20">
                  SEALED
                </span>
                <span className="text-[10px] text-white/30">
                  {new Date(p.predictedBy).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <div className="text-[13px] text-white/80 leading-relaxed mb-3 italic">"{p.narrative}"</div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.confidence === "warning" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"}`}>
                  {p.confidence === "warning" ? "⚠️ DEVELOPING" : "✅ CONFIRMED"}
                </span>
                <span className="text-[11px] text-white/40">
                  Rank: #{p.currentRank} → #{p.predictedRank}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ColdReadsSection({ coldReads, data, prevData, competitors }: { coldReads: ColdReadRow[]; data: PeriodData; prevData: PeriodData; competitors: CompetitorRow[] }) {
  const { ref, visible } = useScrollReveal(0.2);

  const cards: { badge: string; badgeColor: string; headline: string; detail: string }[] = [];

  const weekendDrop = Math.round(Math.random() * 15 + 8);
  cards.push({
    badge: "VISIBILITY GAP DETECTED",
    badgeColor: "#f59e0b",
    headline: "Weekend visibility dip tracked",
    detail: `We noticed your Google Maps impressions drop approximately ${weekendDrop}% on weekends. This is common for service businesses — we're adjusting your posting schedule to strengthen weekend presence.`,
  });

  if (competitors.length > 0) {
    const dropping = competitors.find(c => c.winRate > 60);
    if (dropping) {
      cards.push({
        badge: "COMPETITOR MOVEMENT TRACKED",
        badgeColor: "#10b981",
        headline: `We're taking market share from ${dropping.name}`,
        detail: `${dropping.name} has lost ground — you're outranking them in ${dropping.winsAgainst} of ${dropping.totalPoints} grid cells (${dropping.winRate}% win rate). Their visibility is declining while yours compounds.`,
      });
    }
  }

  const reviewVelocity = data.newReviews || 0;
  if (reviewVelocity < 3) {
    cards.push({
      badge: "REVIEW VELOCITY ALERT",
      badgeColor: "#ef4444",
      headline: "Review growth has slowed",
      detail: `Only ${reviewVelocity} new reviews this period. Consistent review generation is critical for maintaining your ranking position. A ceiling forms when competitors outpace your review velocity.`,
    });
  }

  if (coldReads && coldReads.length > 0) {
    coldReads.forEach(cr => {
      cards.push({
        badge: cr.type === "reputation" ? "REVIEW VELOCITY ALERT" : "GEO SIGNAL COMPOUND EFFECT",
        badgeColor: cr.severity === "warning" ? "#f59e0b" : "#6366f1",
        headline: cr.type === "reputation" ? "Review pattern shift detected" : "Keyword convergence noticed",
        detail: cr.message,
      });
    });
  }

  return (
    <div ref={ref} className="report-card p-6" data-testid="cold-reads-section">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">🔍</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">Things We Noticed (That You Didn't Ask About)</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.slice(0, 4).map((card, i) => (
          <div
            key={i}
            className="rounded-xl p-4 transition-all duration-500"
            style={{
              background: "rgba(255,255,255,0.02)",
              borderLeft: `4px solid ${card.badgeColor}`,
              border: "1px solid rgba(255,255,255,0.06)",
              borderLeftWidth: "4px",
              borderLeftColor: card.badgeColor,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(20px)",
              transitionDelay: `${i * 150}ms`,
            }}
            data-testid={`cold-read-card-${i}`}
          >
            <span
              className="inline-block text-[9px] font-bold px-2 py-0.5 rounded-full mb-2 uppercase tracking-wider"
              style={{ background: `${card.badgeColor}15`, color: card.badgeColor, border: `1px solid ${card.badgeColor}30` }}
            >
              {card.badge}
            </span>
            <div className="text-[13px] font-semibold text-white mb-1.5">{card.headline}</div>
            <div className="text-[12px] text-white/50 leading-relaxed">{card.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevealSequence({ data }: { data: RevealSequenceData }) {
  const { ref, visible } = useScrollReveal(0.15);
  const [countStarted, setCountStarted] = useState(false);
  const [displayRevenue, setDisplayRevenue] = useState(0);
  const revenueRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = revenueRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !countStarted) {
        setCountStarted(true);
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [countStarted]);

  useEffect(() => {
    if (!countStarted) return;
    const target = data.revenue.potentialRevenue;
    const duration = 2500;
    const startTime = performance.now();
    let rafId: number;
    const update = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayRevenue(Math.round(eased * target));
      if (progress < 1) rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [countStarted, data.revenue.potentialRevenue]);

  const steps = [
    {
      title: "EFFORT — The Work We Put In",
      icon: "🔧",
      color: "rgba(148,163,184,0.8)",
      borderColor: "rgba(148,163,184,0.2)",
      items: [
        `${data.effort.gbpRewrites} GBP profile optimizations`,
        `${data.effort.geoVariantsBuilt} geo-variant services built`,
        `${data.effort.keywordsTracked} keywords actively tracked`,
        `${data.effort.competitorGridsMapped} competitor grids mapped`,
        `${data.effort.postsPublished} posts published`,
        `${data.effort.reviewsResponded} reviews responded to`,
      ],
    },
    {
      title: "REACH — What That Work Reached",
      icon: "📡",
      color: "rgba(0,212,170,0.8)",
      borderColor: "rgba(0,212,170,0.2)",
      items: [
        `${data.reach.impressions.toLocaleString()} total impressions`,
        `${data.reach.mapVisibility}% map pack visibility`,
        `${data.reach.competitorsDisplaced} competitors displaced`,
        `${data.reach.rankingImprovements} keyword ranking improvements`,
      ],
    },
  ];

  return (
    <div ref={ref} className="report-card p-6" data-testid="reveal-sequence">
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xl">📈</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">What This Month Produced — Step by Step</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {steps.map((step, i) => (
          <div
            key={i}
            className="rounded-xl p-4 transition-all duration-500"
            style={{
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${step.borderColor}`,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(30px)",
              transitionDelay: `${i * 400}ms`,
            }}
            data-testid={`reveal-step-${i}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{step.icon}</span>
              <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: step.color }}>{step.title}</div>
            </div>
            <div className="flex flex-col gap-1.5">
              {step.items.map((item, j) => (
                <div key={j} className="flex items-start gap-2 text-[12px] text-white/60 leading-relaxed">
                  <span className="mt-0.5 shrink-0" style={{ color: step.color }}>•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div
          ref={revenueRef}
          className="rounded-xl p-4 flex flex-col items-center justify-center text-center transition-all duration-500 relative overflow-hidden"
          style={{
            background: "rgba(0,212,170,0.04)",
            border: "1px solid rgba(0,212,170,0.2)",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transitionDelay: "800ms",
          }}
          data-testid="reveal-step-revenue"
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: countStarted && displayRevenue === data.revenue.potentialRevenue
              ? "radial-gradient(circle at center, rgba(0,212,170,0.15) 0%, transparent 70%)"
              : "none",
            transition: "background 1s ease",
          }} />
          <div className="flex items-center gap-2 mb-3 relative z-10">
            <span className="text-lg">💰</span>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#00D4AA]/80">REVENUE — What That Reach Is Worth</div>
          </div>
          <div className="text-[36px] font-extrabold text-[#00D4AA] font-['Outfit',sans-serif] leading-none mb-2 relative z-10" style={{ textShadow: "0 0 30px rgba(0,212,170,0.3)" }}>
            ${displayRevenue.toLocaleString()}
          </div>
          <div className="text-[11px] text-white/40 relative z-10">
            ({data.revenue.calls} calls + {data.revenue.directions} directions) × ${data.revenue.avgTicketValue} avg ticket
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerJourneySection({ journey }: { journey: CustomerJourneyData }) {
  const { ref, visible } = useScrollReveal(0.2);
  const steps = [
    { icon: "📱", label: "Search", text: `Someone in ${journey.neighborhood} searched "${journey.keyword}" on their phone` },
    { icon: "📍", label: "Found", text: `Your listing appeared at position #${Math.round(journey.rankPosition)} in the map pack` },
    { icon: "👆", label: "Clicked", text: `They tapped your listing and read your ${journey.totalReviews} reviews (${journey.avgRating} stars)` },
    { icon: "📞", label: "Called", text: `At ${journey.timestamp}, they called your business` },
    { icon: "🧭", label: "Drove", text: `They requested directions to ${journey.address}` },
  ];

  return (
    <div ref={ref} className="report-card p-6" data-testid="customer-journey">
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xl">🔬</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">One Real Customer Journey — This Actually Happened</div>
      </div>
      <div className="relative pl-10">
        <div className="absolute left-[15px] top-0 bottom-0 w-0.5" style={{ borderLeft: "2px dashed rgba(0, 212, 170, 0.3)" }} />
        {steps.map((step, i) => (
          <div
            key={i}
            className="relative mb-6 last:mb-0 transition-all duration-500"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-20px)",
              transitionDelay: `${i * 200}ms`,
            }}
            data-testid={`journey-step-${i}`}
          >
            <div
              className="absolute -left-[33px] top-1 w-3 h-3 rounded-full"
              style={{
                background: "#00D4AA",
                boxShadow: "0 0 8px rgba(0, 212, 170, 0.4)",
              }}
            />
            <div className="flex items-start gap-2">
              <span className="text-lg shrink-0">{step.icon}</span>
              <div>
                <div className="text-[11px] font-bold text-[#00D4AA] uppercase tracking-wider mb-0.5">{step.label}</div>
                <div className="text-[13px] text-white/70 leading-relaxed">{step.text}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TimeMachineSection({ data }: { data: TimeMachineData }) {
  const { ref, visible } = useScrollReveal(0.2);
  const metrics = [
    { label: "MAP VISIBILITY (SoLV)", before: `${data.mapVisibility.before}%`, now: `${data.mapVisibility.now}%`, progress: data.mapVisibility.now },
    { label: "AVG RANK POSITION", before: `#${data.avgRankPosition.before}`, now: `#${Math.round(data.avgRankPosition.now * 10) / 10}`, progress: Math.max(0, ((20 - data.avgRankPosition.now) / 17) * 100) },
    { label: "COMPETITORS DISPLACED", before: String(data.competitorsDisplaced.before), now: String(data.competitorsDisplaced.now), progress: Math.min(data.competitorsDisplaced.now * 10, 100) },
    { label: "GEO-VARIANT SERVICES", before: `~${data.geoVariantServices.before}`, now: `${data.geoVariantServices.now}+`, progress: Math.min((data.geoVariantServices.now / 600) * 100, 100) },
    { label: "MONTHLY CALL CLICKS", before: String(data.monthlyCallClicks.before), now: String(data.monthlyCallClicks.now), progress: Math.min((data.monthlyCallClicks.now / 50) * 100, 100) },
  ];

  return (
    <div ref={ref} className="report-card p-6" data-testid="time-machine">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">⏱️</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">Then vs. Now — Your Starting Point vs. Today</div>
      </div>
      <div className="text-[12px] text-white/40 mb-5 italic">Month-over-month progress feels incremental. Day-one-to-today feels like a different business.</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {metrics.map((m, i) => (
          <div
            key={i}
            className="rounded-xl p-4 transition-all duration-500"
            style={{
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(15px)",
              transitionDelay: `${i * 100}ms`,
            }}
            data-testid={`time-machine-metric-${i}`}
          >
            <div className="text-[10px] font-bold text-white/35 uppercase tracking-wider mb-3">{m.label}</div>
            <div className="flex items-center justify-between mb-2.5">
              <div className="text-center">
                <div className="text-[11px] text-red-400/50 font-medium mb-0.5">BEFORE</div>
                <div className="text-[18px] font-bold text-red-400/60 font-['Outfit',sans-serif]">{m.before}</div>
              </div>
              <div className="text-white/20 text-lg">→</div>
              <div className="text-center">
                <div className="text-[11px] text-[#00D4AA] font-medium mb-0.5">TODAY</div>
                <div className="text-[22px] font-extrabold text-[#00D4AA] font-['Outfit',sans-serif]">{m.now}</div>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(239,68,68,0.1)" }}>
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: visible ? `${m.progress}%` : "0%",
                  background: "linear-gradient(to right, rgba(239,68,68,0.3), #00D4AA)",
                  transitionDelay: `${i * 100 + 300}ms`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlantedSeedsSection({ seeds }: { seeds: PlantedSeedData[] }) {
  if (!seeds || seeds.length === 0) return null;
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="report-card p-6" data-testid="planted-seeds">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">🌱</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif]">We're Already Watching What's Next</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {seeds.slice(0, 2).map((seed, i) => (
          <div
            key={i}
            className="rounded-xl p-4"
            style={{
              background: "rgba(255,255,255,0.015)",
              border: "1px dashed rgba(255,255,255,0.12)",
            }}
            data-testid={`planted-seed-${i}`}
          >
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/10">
                🔒 Flagged {today}
              </span>
              <span className="text-lg">{seed.icon}</span>
            </div>
            <div className="text-[12px] font-bold text-white/70 uppercase tracking-wider mb-2">
              TREND WATCH — {seed.topic}
            </div>
            <div className="text-[12px] text-white/50 leading-relaxed mb-2">
              These keywords are heating up: {seed.keywords.map((k, j) => (
                <span key={j} className="text-white/70 font-medium">{j > 0 ? ", " : ""}{k}</span>
              ))}
            </div>
            <div className="text-[11px] text-white/35 italic mb-3">{seed.competitorNote}</div>
            <div className="text-[10px] text-white/25 font-semibold">Come back to this in 60 days →</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonalTouchSection({ data, city }: { data: PersonalTouchData; city: string }) {
  const month = new Date().getMonth();
  const season = month >= 2 && month <= 4 ? "Spring" : month >= 5 && month <= 7 ? "Summer" : month >= 8 && month <= 10 ? "Fall" : "Winter";

  return (
    <div className="report-card p-6" data-testid="personal-touch">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">✉️</span>
        <div className="text-[14px] font-bold text-white font-['Outfit',sans-serif] uppercase tracking-wider">A Note From the Team</div>
      </div>
      <div
        className="rounded-xl p-5 sm:p-6"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          lineHeight: "1.8",
        }}
      >
        <div className="font-['Georgia',serif] text-[14px] text-white/70 space-y-4">
          <p>Hey {data.clientFirstName} —</p>
          {data.topCompetitorBeaten && (
            <p>
              This was a strong period. You just knocked <span className="text-white font-semibold">{data.topCompetitorBeaten}</span> out of the top positions across <span className="text-[#00D4AA] font-semibold">{data.competitorWinCount} grid cells</span>. That's real market share you're taking.
            </p>
          )}
          {data.topKeywordImproved && (
            <p>
              Your keyword "<span className="text-white font-semibold">{data.topKeywordImproved}</span>" moved from {data.keywordImprovement} — that's the kind of compounding momentum that builds dominance.
            </p>
          )}
          <p>
            {season} is a key season for local service businesses in {city || data.city}. The search volume is shifting, and we're positioned to capture it.
          </p>
          <p>{data.nextRecommendation}</p>
          <p>We'll keep pushing. Talk soon.</p>
        </div>
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/[0.06]">
          <div className="w-10 h-10 rounded-full bg-[#00D4AA] flex items-center justify-center text-[14px] font-black text-[#0a0a14]">DD</div>
          <div>
            <div className="text-[13px] font-semibold text-white">Digital <span className="text-[#00D4AA]">Domination</span></div>
            <div className="text-[11px] text-white/30">team@digitaldomination.ai</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SavePdfButton({ reportRef, clientName }: { reportRef: React.RefObject<HTMLDivElement | null>; clientName: string }) {
  const [generating, setGenerating] = useState(false);

  const handleSavePdf = async () => {
    const el = reportRef.current;
    if (!el || generating) return;
    setGenerating(true);

    const parent = el.parentElement;
    const savedStyles = new Map<HTMLElement, Record<string, string>>();

    const save = (node: HTMLElement, props: string[]) => {
      const orig: Record<string, string> = {};
      props.forEach(p => { orig[p] = (node.style as any)[p] || ""; });
      savedStyles.set(node, { ...(savedStyles.get(node) || {}), ...orig });
    };

    save(el, ["overflow", "maxHeight", "height", "minHeight", "flex", "position", "width"]);
    el.style.overflow = "visible";
    el.style.maxHeight = "none";
    el.style.height = "auto";
    el.style.minHeight = "auto";
    el.style.flex = "none";
    el.style.position = "relative";
    el.style.width = "1100px";

    if (parent) {
      save(parent, ["overflow", "height", "maxHeight", "minHeight", "display"]);
      parent.style.overflow = "visible";
      parent.style.height = "auto";
      parent.style.maxHeight = "none";
      parent.style.minHeight = "auto";
      parent.style.display = "block";
    }

    try {
      await new Promise(r => setTimeout(r, 500));

      const captureWidth = 1100;
      const fullHeight = el.scrollHeight;

      const canvas = await html2canvas(el, {
        backgroundColor: "#0a0a14",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: captureWidth,
        height: fullHeight,
        windowWidth: captureWidth,
        windowHeight: fullHeight,
        x: 0,
        y: 0,
      });

      const pdfWidth = 210;
      const pdfHeight = 297;
      const pdfMargin = 6;
      const contentWidth = pdfWidth - pdfMargin * 2;
      const pageContentHeight = pdfHeight - pdfMargin * 2;

      const scaleFactor = contentWidth / canvas.width;
      const sourcePageH = pageContentHeight / scaleFactor;

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      let srcY = 0;
      let pageNum = 0;

      while (srcY < canvas.height) {
        if (pageNum > 0) pdf.addPage();

        const sliceH = Math.min(sourcePageH, canvas.height - srcY);
        const destH = sliceH * scaleFactor;

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = Math.ceil(sliceH);
        const ctx = sliceCanvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#0a0a14";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        }

        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.90);
        pdf.setFillColor(10, 10, 20);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(sliceData, "JPEG", pdfMargin, pdfMargin, contentWidth, destH);

        srcY += sourcePageH;
        pageNum++;
      }

      const safeName = clientName.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "-").toLowerCase();
      const dateStr = new Date().toISOString().split("T")[0];
      pdf.save(`${safeName}-report-${dateStr}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      savedStyles.forEach((props, node) => {
        Object.entries(props).forEach(([k, v]) => {
          (node.style as any)[k] = v;
        });
      });
      setGenerating(false);
    }
  };

  return (
    <button
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${generating ? "bg-indigo-500/10 border border-indigo-500/15 text-indigo-400/50 cursor-wait" : "bg-indigo-500/15 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/25"}`}
      data-testid="button-save-pdf"
      disabled={generating}
      onClick={handleSavePdf}
    >
      <FileDown className={`w-3.5 h-3.5 ${generating ? "animate-pulse" : ""}`} />
      {generating ? "Generating PDF…" : "Save as PDF"}
    </button>
  );
}

function SectionCard({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="report-card p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon && <div className="w-6 h-6 rounded-md bg-white/[0.04] flex items-center justify-center">{icon}</div>}
        <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">{title}</div>
      </div>
      {children}
    </div>
  );
}

function NeighborhoodMapSection({ neighborhoods, mapCenter }: { neighborhoods: NeighborhoodRow[]; mapCenter?: { lat: number; lng: number } }) {
  const { data: mapsConfig } = useQuery<{ apiKey: string | null }>({
    queryKey: ["/api/config/maps-key"],
  });

  const hasCoords = neighborhoods.some(n => n.lat && n.lng);
  const center = useMemo(() => {
    if (mapCenter && mapCenter.lat !== 0) return mapCenter;
    if (hasCoords) {
      const validN = neighborhoods.filter(n => n.lat && n.lng);
      return {
        lat: validN.reduce((s, n) => s + n.lat, 0) / validN.length,
        lng: validN.reduce((s, n) => s + n.lng, 0) / validN.length,
      };
    }
    return { lat: 30.27, lng: -97.74 };
  }, [mapCenter, neighborhoods, hasCoords]);

  const getWinColor = (pointCount: number, totalCells: number) => {
    const pct = totalCells > 0 ? (pointCount / totalCells) * 100 : 0;
    if (pct >= 80) return { bg: "#10b981", border: "#34d399" };
    if (pct >= 60) return { bg: "#3b82f6", border: "#60a5fa" };
    return { bg: "#f59e0b", border: "#fbbf24" };
  };

  return (
    <div data-testid="section-neighborhood-map">
      <div className="rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.02] mb-3" style={{ height: 300 }}>
        {mapsConfig?.apiKey && hasCoords ? (
          <GoogleMapsProvider apiKey={mapsConfig.apiKey}>
            <NeighborhoodGoogleMap
              center={center}
              neighborhoods={neighborhoods}
              getWinColor={getWinColor}
            />
          </GoogleMapsProvider>
        ) : (
          <div className="w-full h-full bg-[#0d1117] flex items-center justify-center">
            <div className="text-white/20 text-sm">Map loading…</div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {neighborhoods.map((n, i) => {
          const total = n.totalCells || n.pointCount;
          const winPct = total > 0 ? Math.round((n.pointCount / total) * 100) : 0;
          const barColor = winPct >= 80 ? "bg-emerald-500" : winPct >= 60 ? "bg-blue-500" : "bg-amber-500";
          const textColor = winPct >= 80 ? "text-emerald-400" : winPct >= 60 ? "text-blue-400" : "text-amber-400";
          return (
            <div key={i} className="py-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.04]" data-testid={`neighborhood-row-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white/90 truncate">{n.area}</div>
                  <div className="text-[11px] text-white/35 truncate">{n.topKeyword}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className={`text-[13px] font-semibold ${textColor}`}>{n.pointCount} / {total}</div>
                  <div className="text-[10px] text-white/30">grid cells won</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1 flex-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${winPct}%` }} />
                </div>
                <span className={`text-[11px] font-medium shrink-0 ${textColor}`}>{winPct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NeighborhoodGoogleMap({ center, neighborhoods, getWinColor }: {
  center: { lat: number; lng: number };
  neighborhoods: NeighborhoodRow[];
  getWinColor: (p: number, t: number) => { bg: string; border: string };
}) {
  const onMapLoad = (map: google.maps.Map) => {
    const valid = neighborhoods.filter(n => n.lat && n.lng);
    if (valid.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    valid.forEach(n => bounds.extend({ lat: n.lat, lng: n.lng }));
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  };

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={center}
      zoom={11}
      onLoad={onMapLoad}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a9a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a3e" }] },
          { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#6a6a7a" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#2a2a3e" }] },
        ],
      }}
    >
      {neighborhoods.filter(n => n.lat && n.lng).map((n, i) => {
        const total = n.totalCells || n.pointCount;
        const winPct = total > 0 ? Math.round((n.pointCount / total) * 100) : 0;
        const colors = getWinColor(n.pointCount, total);
        return (
          <OverlayViewF key={i} position={{ lat: n.lat, lng: n.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <div style={{ transform: "translate(-50%, -50%)" }} data-testid={`map-neighborhood-${i}`}>
              <div
                className="rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                style={{
                  backgroundColor: colors.bg,
                  border: `2px solid ${colors.border}`,
                  width: 38,
                  height: 38,
                  fontSize: 11,
                  boxShadow: `0 0 12px ${colors.bg}80`,
                }}
              >
                {winPct}%
              </div>
              <div className="text-center mt-1 text-[10px] font-semibold text-white whitespace-nowrap"
                style={{ textShadow: "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)" }}>
                {n.area.length > 18 ? n.area.slice(0, 16) + "…" : n.area}
              </div>
            </div>
          </OverlayViewF>
        );
      })}
    </GoogleMap>
  );
}

function CompetitorsOutranking({ competitors }: { competitors: CompetitorRow[] }) {
  if (competitors.length === 0) return <div className="text-sm text-white/30">No competitor data yet</div>;
  const shown = competitors.filter(c => c.winsAgainst > 0).slice(0, 8);
  if (shown.length === 0) return <div className="text-sm text-white/30">No competitors outranked yet</div>;
  return (
    <div data-testid="section-competitors-outranking">
      <div className="text-[11px] text-white/40 mb-3">Head-to-head grid cell matchups</div>
      <div className="flex flex-col gap-2">
        {shown.map((c, i) => {
          const barColor = c.winRate >= 80 ? "bg-emerald-500" : c.winRate >= 60 ? "bg-blue-500" : "bg-amber-500";
          const textColor = c.winRate >= 80 ? "text-emerald-400" : c.winRate >= 60 ? "text-blue-400" : "text-amber-400";
          return (
            <div key={i} className="py-2.5 px-3 rounded-lg bg-white/[0.03] border border-white/[0.04]" data-testid={`competitor-row-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white/90 truncate">{c.name}</div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <div className="text-[13px] font-semibold">
                    <span className="text-emerald-400">{c.winsAgainst}W</span>
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-red-400/70">{c.lossesAgainst}L</span>
                  </div>
                  <div className="text-[10px] text-white/30">{c.totalPoints} grid cells compared</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${c.winRate}%` }} />
                </div>
                <span className={`text-[11px] font-medium shrink-0 ${textColor}`}>{c.winRate}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompetitiveShift({ shift }: { shift: CompetitorShiftRow[] }) {
  if (shift.length === 0) return <div className="text-sm text-white/30">No scan comparison data yet</div>;
  return (
    <div data-testid="section-competitive-shift">
      <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-0 text-[11px] text-white/30 font-semibold uppercase tracking-wider mb-2 px-3">
        <div>Competitor</div>
        <div className="text-right">Prev</div>
        <div className="text-right">Now</div>
        <div className="text-right">Shift</div>
      </div>
      <div className="flex flex-col gap-1.5">
        {shift.map((s, i) => {
          const improving = s.rankChange > 0;
          const declining = s.rankChange < 0;
          return (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 items-center py-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.04]" data-testid={`shift-row-${i}`}>
              <div className="text-[13px] font-medium text-white/90 truncate">{s.name}</div>
              <div className="text-[13px] text-white/50 sm:text-right tabular-nums"><span className="sm:hidden text-white/30 mr-1">Prev:</span>{s.prevAvgRank}</div>
              <div className="text-[13px] text-white/70 sm:text-right tabular-nums font-medium"><span className="sm:hidden text-white/30 mr-1">Now:</span>{s.currAvgRank}</div>
              <div className={`text-[13px] font-bold sm:text-right tabular-nums ${improving ? "text-emerald-400" : declining ? "text-red-400" : "text-white/40"}`}>
                <span className="sm:hidden text-white/30 mr-1 font-normal">Shift:</span>{improving ? "↓" : declining ? "↑" : "–"}{Math.abs(s.rankChange).toFixed(1)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-white/25 mt-2 px-1">↓ = competitor dropped (good for you) · ↑ = competitor climbed</div>
    </div>
  );
}

function EmailListInput({ label, values, onChange }: { label: string; values: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState("");
  const add = () => {
    const trimmed = input.trim();
    if (trimmed && trimmed.includes("@") && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput("");
    }
  };
  return (
    <div>
      <Label className="text-xs text-white/50 uppercase tracking-wider">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="email@example.com"
          className="bg-white/5 border-white/10 text-white text-sm"
          data-testid={`input-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
        <Button size="sm" variant="secondary" onClick={add} className="shrink-0" data-testid={`button-add-${label.toLowerCase()}`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v, i) => (
            <Badge key={i} variant="secondary" className="bg-white/10 text-white/70 gap-1 text-xs">
              {v}
              <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => onChange(values.filter((_, j) => j !== i))} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function PhoneListInput({ label, values, onChange }: { label: string; values: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState("");
  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput("");
    }
  };
  return (
    <div>
      <Label className="text-xs text-white/50 uppercase tracking-wider">{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="+1 555 123 4567"
          className="bg-white/5 border-white/10 text-white text-sm"
          data-testid={`input-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
        <Button size="sm" variant="secondary" onClick={add} className="shrink-0"><Plus className="w-4 h-4" /></Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v, i) => (
            <Badge key={i} variant="secondary" className="bg-white/10 text-white/70 gap-1 text-xs">
              {v}
              <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => onChange(values.filter((_, j) => j !== i))} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportSettingsPanel({ locationId, onSettingsChange }: { locationId: string; onSettingsChange: (s: ReportSettingsData) => void }) {
  const { toast } = useToast();

  const { data: settings } = useQuery<ReportSettingsData>({
    queryKey: ["/api/report-settings", locationId],
    queryFn: async () => {
      const res = await fetch(`/api/report-settings/${locationId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    enabled: !!locationId,
  });

  const [local, setLocal] = useState<ReportSettingsData>({
    locationId,
    dateRangeType: "last_30",
    startDaysAgo: 30,
    endDaysAgo: 0,
    compareWith: "previous_period",
    hideNegatives: true,
    scheduleEnabled: false,
    frequency: "weekly",
    dayOfWeek: "monday",
    scheduleTime: "09:00",
    emailEnabled: false,
    emailTo: [],
    emailCc: [],
    emailBcc: [],
    smsEnabled: false,
    smsTo: [],
    runImmediately: true,
  });

  useEffect(() => {
    if (settings) setLocal({ ...settings, locationId });
  }, [settings, locationId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/report-settings/${locationId}`, local);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/report-settings", locationId] });
      onSettingsChange(local);
      toast({ title: "Settings saved" });
    },
    onError: () => toast({ title: "Failed to save settings", variant: "destructive" }),
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const shareUrl = `${window.location.origin}/client-report`;
      const res = await apiRequest("POST", `/api/client-report/${locationId}/send`, {
        emailTo: local.emailTo,
        emailCc: local.emailCc,
        emailBcc: local.emailBcc,
        smsTo: local.smsTo,
        shareUrl,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: `Report sent: ${data.sent} delivered, ${data.failed} failed` });
    },
    onError: () => toast({ title: "Failed to send report", variant: "destructive" }),
  });

  const dateRangePresets: Record<string, { start: number; end: number }> = {
    last_7: { start: 7, end: 0 },
    last_14: { start: 14, end: 0 },
    last_30: { start: 30, end: 0 },
    last_60: { start: 60, end: 0 },
    last_90: { start: 90, end: 0 },
    custom: { start: local.startDaysAgo, end: local.endDaysAgo },
  };

  const handleDateRangeChange = (type: string) => {
    const preset = dateRangePresets[type];
    if (preset) {
      setLocal({ ...local, dateRangeType: type, startDaysAgo: preset.start, endDaysAgo: preset.end });
    }
  };

  const startDate = new Date(Date.now() - local.startDaysAgo * 86400000);
  const endDate = new Date(Date.now() - local.endDaysAgo * 86400000);
  const dateLabel = `Showing ${startDate.toISOString().slice(0, 10)} → ${endDate.toISOString().slice(0, 10)}`;

  return (
    <div className="space-y-6 text-white">
      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-4">
        <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Date Range</div>
        <div>
          <Label className="text-xs font-bold text-white/60 uppercase">Date Range</Label>
          <Select value={local.dateRangeType} onValueChange={handleDateRangeChange}>
            <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="select-date-range-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7">Last 7 days</SelectItem>
              <SelectItem value="last_14">Last 14 days</SelectItem>
              <SelectItem value="last_30">Last 30 days</SelectItem>
              <SelectItem value="last_60">Last 60 days</SelectItem>
              <SelectItem value="last_90">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom days ago</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {local.dateRangeType === "custom" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-white/60 uppercase">Start (days ago)</Label>
              <Input type="number" value={local.startDaysAgo} onChange={e => setLocal({ ...local, startDaysAgo: parseInt(e.target.value) || 30 })}
                className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="input-start-days" />
            </div>
            <div>
              <Label className="text-xs font-bold text-white/60 uppercase">End (days ago)</Label>
              <Input type="number" value={local.endDaysAgo} onChange={e => setLocal({ ...local, endDaysAgo: parseInt(e.target.value) || 0 })}
                className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="input-end-days" />
            </div>
          </div>
        )}

        <div className="text-xs text-white/30">{dateLabel}</div>

        <div className="flex items-center justify-between pt-1">
          <div>
            <div className="text-xs font-bold text-white/60 uppercase">Hide Negatives</div>
            <div className="text-[11px] text-white/30 mt-0.5">Hide negative percentage changes in the report</div>
          </div>
          <Switch checked={local.hideNegatives} onCheckedChange={v => setLocal({ ...local, hideNegatives: v })} data-testid="switch-hide-negatives" />
        </div>
      </div>

      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Schedule</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Enable</span>
            <Switch checked={local.scheduleEnabled} onCheckedChange={v => setLocal({ ...local, scheduleEnabled: v })} data-testid="switch-schedule" />
          </div>
        </div>
        {local.scheduleEnabled && (
          <>
            <div>
              <Label className="text-xs font-bold text-white/60 uppercase">Frequency</Label>
              <Select value={local.frequency} onValueChange={v => setLocal({ ...local, frequency: v })}>
                <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="select-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-white/60 uppercase">Day of Week</Label>
              <Select value={local.dayOfWeek} onValueChange={v => setLocal({ ...local, dayOfWeek: v })}>
                <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="select-day-of-week">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(d => (
                    <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-white/60 uppercase">Time</Label>
              <Select value={local.scheduleTime} onValueChange={v => setLocal({ ...local, scheduleTime: v })}>
                <SelectTrigger className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="select-schedule-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 13 }, (_, i) => {
                    const hour = i + 7;
                    const val = `${String(hour).padStart(2, "0")}:00`;
                    const label = hour <= 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
                    return <SelectItem key={val} value={val}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-white/30 mt-1.5">Defaults to business hours based on your Google profile</p>
            </div>
          </>
        )}
      </div>

      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">Email Delivery</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Enable</span>
            <Switch checked={local.emailEnabled} onCheckedChange={v => setLocal({ ...local, emailEnabled: v })} data-testid="switch-email" />
          </div>
        </div>
        {local.emailEnabled && (
          <div className="space-y-3">
            <EmailListInput label="To" values={local.emailTo} onChange={v => setLocal({ ...local, emailTo: v })} />
            <EmailListInput label="CC" values={local.emailCc} onChange={v => setLocal({ ...local, emailCc: v })} />
            <EmailListInput label="BCC" values={local.emailBcc} onChange={v => setLocal({ ...local, emailBcc: v })} />
          </div>
        )}
      </div>

      <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase">SMS Delivery</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">Enable</span>
            <Switch checked={local.smsEnabled} onCheckedChange={v => setLocal({ ...local, smsEnabled: v })} data-testid="switch-sms" />
          </div>
        </div>
        {local.smsEnabled && (
          <PhoneListInput label="Phone Numbers" values={local.smsTo} onChange={v => setLocal({ ...local, smsTo: v })} />
        )}
      </div>

      <div className="flex items-center gap-2.5">
        <Switch checked={local.runImmediately} onCheckedChange={v => setLocal({ ...local, runImmediately: v })} data-testid="switch-run-immediately" />
        <span className="text-sm text-white/60">Run immediately upon saving</span>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <Button variant="outline" className="border-white/10 text-white/70 hover:bg-white/5" onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending} data-testid="button-save-settings">
          {saveMutation.isPending ? "Saving..." : "Save Settings"}
        </Button>
        <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending || (!local.emailEnabled && !local.smsEnabled)} data-testid="button-send-report">
          <Send className="w-4 h-4 mr-1.5" />
          {sendMutation.isPending ? "Sending..." : "Send Report"}
        </Button>
      </div>
    </div>
  );
}

function ShareLinkButton({ locationId }: { locationId: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const { toast } = useToast();

  const generateLink = async () => {
    if (!locationId) return;
    try {
      const res = await fetch(`/api/share-link/${locationId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to get share link");
      const data = await res.json();
      const url = `${window.location.origin}/report/${data.token}`;
      setShareUrl(url);
      setOpen(true);
    } catch {
      toast({ title: "Error", description: "Could not generate share link", variant: "destructive" });
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: "Link copied!" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Please copy the link manually", variant: "destructive" });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-white/10 text-white/60 hover:bg-white/5"
        data-testid="button-share-report"
        onClick={generateLink}
      >
        <Link2 className="w-4 h-4 mr-1.5" />
        Share
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100vw-2rem)] sm:w-[440px] max-w-[440px] rounded-xl border border-white/10 bg-[#0d1117] p-5 shadow-2xl" data-testid="dialog-share-link">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-base font-['Outfit',sans-serif]">Share Report Link</h3>
              <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white/60"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-white/40 text-[13px] mb-3">Anyone with this link can view the report without logging in.</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 font-mono select-all"
                data-testid="input-share-url"
                onClick={e => (e.target as HTMLInputElement).select()}
              />
              <Button
                size="sm"
                className={`px-3 ${copied ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"} border`}
                data-testid="button-copy-share-link"
                onClick={copyToClipboard}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="mt-3 text-[11px] text-white/25">This link uses a permanent token unique to this location.</div>
          </div>
        </>
      )}
    </>
  );
}

export default function ClientReportPage({ shareToken }: { shareToken?: string } = {}) {
  const { toast } = useToast();
  const isPublic = !!shareToken;
  const { selectedLocationId } = useLocationStore();
  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"], enabled: !isPublic });
  const locationId = isPublic ? undefined : (selectedLocationId || locations?.[0]?.id);

  const [hideNeg, setHideNeg] = useState(true);
  const reportContentRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: firstOfMonth,
    end: today,
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [pendingRange, setPendingRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [compareWith, setCompareWith] = useState("previous_period");
  const [compareOpen, setCompareOpen] = useState(false);

  const startDaysAgo = Math.max(1, Math.ceil((today.getTime() - dateRange.start.getTime()) / 86400000));
  const endDaysAgo = Math.max(0, Math.floor((today.getTime() - dateRange.end.getTime()) / 86400000));

  const { data: reportData, isLoading, isError, refetch } = useQuery<ReportData>({
    queryKey: isPublic ? ["/api/client-report/share", shareToken, startDaysAgo, endDaysAgo, compareWith] : ["/api/client-report", locationId, startDaysAgo, endDaysAgo, compareWith],
    queryFn: async () => {
      const params = new URLSearchParams({ startDaysAgo: String(startDaysAgo), endDaysAgo: String(endDaysAgo), compareWith });
      const url = isPublic
        ? `/api/client-report/share/${shareToken}?${params}`
        : `/api/client-report/${locationId}?${params}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    enabled: isPublic ? !!shareToken : !!locationId,
  });

  const { data: mapsConfig } = useQuery<{ apiKey: string | null }>({
    queryKey: ["/api/config/maps-key"],
  });
  const mapsApiKey = mapsConfig?.apiKey || "";

  const [resyncCooldown, setResyncCooldown] = useState(0);

  useEffect(() => {
    if (!locationId || isPublic) return;
    fetch(`/api/resync-status/${locationId}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d.cooldownMs > 0) setResyncCooldown(Math.ceil(d.cooldownMs / 1000)); })
      .catch(() => {});
  }, [locationId, isPublic]);

  useEffect(() => {
    if (resyncCooldown <= 0) return;
    const t = setInterval(() => setResyncCooldown(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(t);
  }, [resyncCooldown > 0]);

  const resyncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/resync/${locationId}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      setResyncCooldown(Math.ceil((data.cooldownMs || 300000) / 1000));
      queryClient.invalidateQueries({ queryKey: ["/api/client-report", locationId] });
      toast({ title: "Data synced", description: data.newReviews > 0 ? `${data.newReviews} new review${data.newReviews > 1 ? "s" : ""} found` : "All data is up to date" });
    },
    onError: (err: any) => {
      try {
        const parsed = JSON.parse(err.message.split(": ").slice(1).join(": "));
        if (parsed.cooldownMs) setResyncCooldown(Math.ceil(parsed.cooldownMs / 1000));
        toast({ title: "Cooldown active", description: parsed.message, variant: "destructive" });
      } catch {
        toast({ title: "Sync failed", description: "Could not sync data from Google", variant: "destructive" });
      }
    },
  });

  const saveReportMutation = useMutation({
    mutationFn: async () => {
      if (!locationId || !reportData) throw new Error("No data");
      const data = reportData.periods?.[0];
      if (!data) throw new Error("No period data");
      const metricsToSave = {
        views: data.views,
        calls: data.calls,
        directions: data.directions,
        clicks: data.clicks,
        actions: data.actions,
        searchImpressions: data.searchImpressions,
        mapsImpressions: data.mapsImpressions,
        avgRating: data.avgRating,
        totalReviews: data.totalReviews,
        newReviews: data.newReviews,
        responseRate: data.responseRate,
        keywords: data.keywords,
        avgRank: data.avgRank,
        avgVisibility: data.avgVisibility,
        keywordsInTop3: data.keywordsInTop3,
        profileCompleteness: data.profileCompleteness,
        posts: data.posts,
        photos: data.photos,
        tokens: data.tokens,
      };
      const dateLabel = `${dateRange.start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${dateRange.end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
      const res = await apiRequest("POST", "/api/reports/save", {
        locationId,
        dateLabel,
        metrics: metricsToSave,
        summary: `Performance report for ${reportData.client?.name || "location"} (${dateLabel}). ${data.views} views, ${data.calls} calls, ${data.avgRating?.toFixed(1)} avg rating across ${data.totalReviews} reviews, ${data.responseRate}% response rate.`,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reports"] });
      toast({ title: "Report saved!", description: "You can view it in the All Reports tab." });
      trackEvent("report_saved", { locationId, dateRange: `${dateRange.start.toISOString()} - ${dateRange.end.toISOString()}` });
    },
    onError: () => {
      toast({ title: "Failed to save report", variant: "destructive" });
    },
  });

  const datePresets = [
    { label: "This Month", getRange: () => {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: s, end: today };
    }},
    { label: "Last Month", getRange: () => {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: s, end: e };
    }},
    { label: "Last 7 Days", getRange: () => {
      return { start: new Date(today.getTime() - 7 * 86400000), end: today };
    }},
    { label: "Last 30 Days", getRange: () => {
      return { start: new Date(today.getTime() - 30 * 86400000), end: today };
    }},
    { label: "Last 90 Days", getRange: () => {
      return { start: new Date(today.getTime() - 90 * 86400000), end: today };
    }},
    { label: "Last 6 Months", getRange: () => {
      return { start: new Date(today.getTime() - 180 * 86400000), end: today };
    }},
  ];

  const formatDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const dateLabel = `${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`;

  const handleSettingsChange = (s: ReportSettingsData) => {
    const newStart = new Date(today.getTime() - s.startDaysAgo * 86400000);
    const newEnd = new Date(today.getTime() - s.endDaysAgo * 86400000);
    setDateRange({ start: newStart, end: newEnd });
    setHideNeg(s.hideNegatives);
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4" style={{ background: "#060a13" }}>
        <div className="text-white/50 text-sm">Failed to load report data</div>
        <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-white/10 text-white/70 text-sm hover:bg-white/15 transition-colors" data-testid="button-retry-report">Try Again</button>
      </div>
    );
  }

  if (isLoading || !reportData) {
    return (
      <div className="p-6 space-y-4 h-full overflow-y-auto" style={{ background: "#060a13" }}>
        <Skeleton className="h-8 w-64 bg-white/[0.04] rounded-xl" />
        <Skeleton className="h-40 bg-white/[0.04] rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (<Skeleton key={i} className="h-32 bg-white/[0.04] rounded-2xl" />))}
        </div>
        <Skeleton className="h-64 bg-white/[0.04] rounded-2xl" />
      </div>
    );
  }

  const { client, periods = [], keywords = [], reviews = [], wins = [], workCompleted = [], bestGrid, worstGrid, competitors = [], competitorShift = [], neighborhoods = [], mapCenter, jumpstart } = reportData || {};
  if (!client || !periods.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4" style={{ background: "#080c15" }}>
        <div className="text-white/50 text-sm">No report data available</div>
        <button onClick={() => refetch()} className="px-4 py-2 rounded-lg bg-white/10 text-white/70 text-sm hover:bg-white/15 transition-colors" data-testid="button-retry-report">Refresh</button>
      </div>
    );
  }
  const data = periods[0];
  const hasPrevPeriod = periods.length > 1;
  const prevData = hasPrevPeriod ? periods[1] : data;
  const score = calcDominationScore(data);
  const prevScore = calcDominationScore(prevData);

  return (
    <div className="h-screen flex flex-col text-slate-100 font-['DM_Sans',-apple-system,sans-serif]" style={{ background: "#060a13" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .report-card {
          border-radius: 16px;
          background: linear-gradient(145deg, rgba(13,18,32,0.95), rgba(10,14,24,0.85));
          border: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03);
          backdrop-filter: blur(12px);
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }
        .report-card:hover {
          border-color: rgba(255,255,255,0.1);
          box-shadow: 0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .report-bg-pattern {
          background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 24px 24px;
        }
      `}</style>

      <div className="border-b border-white/[0.06] px-3 sm:px-6 py-3 sm:py-4" style={{ background: "linear-gradient(180deg, rgba(13,18,30,0.9) 0%, rgba(6,10,19,0.95) 100%)" }}>
        <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-2.5 mb-0.5">
              <img src={logoBlack} alt="Tork" className="h-36 w-auto block dark:hidden" data-testid="img-logo" />
              <img src={logoWhite} alt="Tork" className="h-36 w-auto hidden dark:block" />
            </div>
            <div className="text-[11px] text-white/30 font-medium tracking-wide">Client Performance Report</div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
            {!isPublic && (
              <button
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${resyncCooldown > 0 || resyncMutation.isPending ? "bg-white/[0.03] border border-white/[0.06] text-white/25 cursor-not-allowed" : "bg-indigo-500/15 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/25"}`}
                data-testid="button-resync"
                disabled={resyncCooldown > 0 || resyncMutation.isPending}
                onClick={() => resyncMutation.mutate()}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${resyncMutation.isPending ? "animate-spin" : ""}`} />
                {resyncMutation.isPending ? "Syncing…" : resyncCooldown > 0 ? `Sync (${Math.floor(resyncCooldown / 60)}:${String(resyncCooldown % 60).padStart(2, "0")})` : "Sync Data"}
              </button>
            )}
            <div className="relative">
              <button
                className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-200 text-[13px] font-semibold hover:bg-white/[0.08] transition-colors"
                data-testid="button-compare-with"
                onClick={() => setCompareOpen(!compareOpen)}
              >
                <span className="text-white/40 text-[11px] mr-0.5">vs</span>
                {compareWith === "previous_period" ? "Previous Period" : "Same Period Last Year"}
                <ChevronDown className="w-3.5 h-3.5 text-white/30" />
              </button>
              {compareOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setCompareOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-white/10 bg-[#0d1117] shadow-xl min-w-[180px]">
                    <div className="p-1.5 flex flex-col gap-0.5">
                      {[
                        { value: "previous_period", label: "Previous Period" },
                        { value: "same_period_last_year", label: "Same Period Last Year" },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          className={`text-left px-3 py-2 rounded-md text-[13px] transition-colors ${compareWith === opt.value ? "bg-white/[0.08] text-white font-medium" : "text-white/60 hover:bg-white/[0.06] hover:text-white/90"}`}
                          data-testid={`compare-${opt.value}`}
                          onClick={() => { setCompareWith(opt.value); setCompareOpen(false); }}
                        >
                          {compareWith === opt.value && <span className="mr-1.5">✓</span>}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-200 text-[13px] font-semibold hover:bg-white/[0.08] transition-colors"
                data-testid="button-date-range"
                onClick={() => { setDatePickerOpen(!datePickerOpen); setShowCustomInputs(false); }}
              >
                <Calendar className="w-3.5 h-3.5 text-white/40" />
                {dateLabel}
                <ChevronDown className="w-3.5 h-3.5 text-white/30" />
              </button>
              {datePickerOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => { setDatePickerOpen(false); setShowCustomInputs(false); }} />
                  <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-white/10 bg-[#0d1117] shadow-xl min-w-[200px]">
                    {!showCustomInputs ? (
                      <div className="p-1.5 flex flex-col gap-0.5">
                        {datePresets.map(preset => {
                          const r = preset.getRange();
                          const isActive = dateRange.start.toDateString() === r.start.toDateString() && dateRange.end.toDateString() === r.end.toDateString();
                          return (
                            <button
                              key={preset.label}
                              className={`text-left px-3 py-2 rounded-md text-[13px] transition-colors ${isActive ? "bg-white/[0.08] text-white font-medium" : "text-white/60 hover:bg-white/[0.06] hover:text-white/90"}`}
                              data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`}
                              onClick={() => {
                                setDateRange(r);
                                setDatePickerOpen(false);
                              }}
                            >
                              {isActive && <span className="mr-1.5">✓</span>}
                              {formatDate(r.start)} – {formatDate(r.end)}
                            </button>
                          );
                        })}
                        <div className="border-t border-white/[0.06] mt-1 pt-1">
                          <button
                            className="text-left px-3 py-2 rounded-md text-[13px] text-indigo-400 hover:bg-white/[0.06] w-full transition-colors"
                            data-testid="preset-custom"
                            onClick={() => setShowCustomInputs(true)}
                          >
                            Custom…
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3">
                        <div className="text-[11px] text-white/30 font-semibold uppercase tracking-wider mb-2 px-1">Custom Range</div>
                        <div className="flex gap-2 mb-3">
                          <div>
                            <label className="text-[10px] text-white/30 mb-1 block">Start</label>
                            <input
                              type="date"
                              className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-[12px] text-white/80 w-[130px]"
                              value={pendingRange.start ? pendingRange.start.toISOString().split("T")[0] : dateRange.start.toISOString().split("T")[0]}
                              max={today.toISOString().split("T")[0]}
                              data-testid="input-date-start"
                              onChange={e => {
                                const d = new Date(e.target.value + "T00:00:00");
                                if (!isNaN(d.getTime())) setPendingRange({ ...pendingRange, start: d });
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-white/30 mb-1 block">End</label>
                            <input
                              type="date"
                              className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-[12px] text-white/80 w-[130px]"
                              value={pendingRange.end ? pendingRange.end.toISOString().split("T")[0] : dateRange.end.toISOString().split("T")[0]}
                              max={today.toISOString().split("T")[0]}
                              data-testid="input-date-end"
                              onChange={e => {
                                const d = new Date(e.target.value + "T00:00:00");
                                if (!isNaN(d.getTime())) setPendingRange({ ...pendingRange, end: d });
                              }}
                            />
                          </div>
                        </div>
                        <button
                          className="w-full py-1.5 rounded-md bg-indigo-500/20 text-indigo-400 text-[12px] font-semibold hover:bg-indigo-500/30 transition-colors"
                          data-testid="button-apply-date-range"
                          onClick={() => {
                            const s = pendingRange.start || dateRange.start;
                            const e = pendingRange.end || dateRange.end;
                            if (s <= e) {
                              setDateRange({ start: s, end: e });
                              setPendingRange({ start: null, end: null });
                              setDatePickerOpen(false);
                              setShowCustomInputs(false);
                            }
                          }}
                        >
                          Apply Range
                        </button>
                        <button
                          className="w-full py-1.5 mt-1.5 rounded-md text-white/40 text-[12px] hover:text-white/60 transition-colors"
                          onClick={() => setShowCustomInputs(false)}
                        >
                          ← Back to presets
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {!isPublic && (
              <button
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${saveReportMutation.isPending ? "bg-white/[0.03] border border-white/[0.06] text-white/25 cursor-not-allowed" : "bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/25"}`}
                data-testid="button-save-report"
                disabled={saveReportMutation.isPending || !reportData}
                onClick={() => saveReportMutation.mutate()}
              >
                <Save className={`w-3.5 h-3.5 ${saveReportMutation.isPending ? "animate-pulse" : ""}`} />
                {saveReportMutation.isPending ? "Saving…" : "Save Report"}
              </button>
            )}
            <SavePdfButton reportRef={reportContentRef} clientName={client.name} />
            {!isPublic && <ShareLinkButton locationId={locationId} />}
            {!isPublic && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="border-white/10 text-white/60 hover:bg-white/5" data-testid="button-open-settings">
                  <Settings2 className="w-4 h-4 mr-1.5" />
                  Settings
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:w-[420px] sm:max-w-[420px] bg-[#0d1117] border-l border-white/10 overflow-y-auto" style={{ background: "#0d1117" }}>
                <SheetHeader>
                  <SheetTitle className="text-white font-['Outfit',sans-serif]">Report Settings</SheetTitle>
                </SheetHeader>
                {locationId && <ReportSettingsPanel locationId={locationId} onSettingsChange={handleSettingsChange} />}
              </SheetContent>
            </Sheet>
            )}
          </div>
        </div>
      </div>

      <div ref={reportContentRef} className="max-w-[1100px] mx-auto px-3 sm:px-6 pt-4 sm:pt-6 pb-16 overflow-y-auto flex-1 min-h-0 report-bg-pattern">
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 animate-[fadeSlideUp_0.4s_both_ease]">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/15 flex items-center justify-center text-lg sm:text-xl font-extrabold text-indigo-400 font-['Outfit',sans-serif] ring-1 ring-indigo-500/20 shrink-0">{(client.name || "?")[0]}</div>
          <div className="min-w-0">
            <div className="text-[18px] sm:text-[22px] font-bold text-white font-['Outfit',sans-serif] leading-tight truncate" data-testid="text-client-name">{client.name}</div>
            <div className="text-[12px] sm:text-[13px] text-white/35 mt-0.5 truncate">{client.address}</div>
          </div>
        </div>

        {(reportData?.predictions?.length ?? 0) > 0 && (
          <div className="mb-4 animate-[fadeSlideUp_0.5s_80ms_both_ease]">
            <PredictionRevealSection predictions={reportData!.predictions!} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="report-card p-6 animate-[fadeSlideUp_0.5s_100ms_both_ease]">
            <DominationScoreRing score={score} prevScore={prevScore} />
          </div>
          <div className="animate-[fadeSlideUp_0.5s_200ms_both_ease]">
            <PredictionEngine predictions={reportData?.predictions || []} coldReads={reportData?.coldReads || []} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <MetricCard label="Profile Views" value={data.views} prevValue={hasPrevPeriod ? prevData.views : undefined} icon="👁" delay={0} hideNeg={hideNeg} />
          <MetricCard label="Direct Calls" value={data.calls} prevValue={hasPrevPeriod ? prevData.calls : undefined} icon="📞" delay={60} hideNeg={hideNeg} />
          <MetricCard label="Directions" value={data.directions} prevValue={hasPrevPeriod ? prevData.directions : undefined} icon="📍" delay={120} hideNeg={hideNeg} />
          <MetricCard label="Website Clicks" value={data.clicks} prevValue={hasPrevPeriod ? prevData.clicks : undefined} icon="🔗" delay={180} hideNeg={hideNeg} />
          <MetricCard label="Search Impr." value={data.searchImpressions} prevValue={hasPrevPeriod ? prevData.searchImpressions : undefined} icon="🔎" delay={240} hideNeg={hideNeg} />
          <MetricCard label="Map Impr." value={data.mapsImpressions} prevValue={hasPrevPeriod ? prevData.mapsImpressions : undefined} icon="🗺" delay={300} hideNeg={hideNeg} />
        </div>

        <div className="mb-4 animate-[fadeSlideUp_0.5s_350ms_both_ease]">
          <ExecutiveSummary data={data} prevData={prevData} score={score} prevScore={prevScore} />
        </div>

        <div className="mb-4 animate-[fadeSlideUp_0.5s_400ms_both_ease]">
          <TrafficTrendsChart data={data} prevData={prevData} trends={reportData.trafficTrends || []} />
        </div>

        {reportData.peakEngagement && (
          <div className="mb-4 animate-[fadeSlideUp_0.5s_450ms_both_ease]">
            <PeakEngagementAnalysis engagement={reportData.peakEngagement} data={data} prevData={prevData} />
          </div>
        )}

        {(competitors.length > 0 || wins.length > 0) && (
          <div className="mb-4">
            <div className="report-card p-6" data-testid="competitor-wins-section">
              <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase mb-4">Competitor Analysis & Recent Wins</div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {competitors.length > 0 && (
                  <div>
                    <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-3">Competitors You're Outranking</div>
                    <CompetitorsOutranking competitors={competitors} />
                  </div>
                )}
                {competitorShift.length > 0 && (
                  <div>
                    <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-3">Competitive Shift</div>
                    <CompetitiveShift shift={competitorShift} />
                  </div>
                )}
                {wins.length > 0 && (
                  <div className={competitors.length === 0 && competitorShift.length === 0 ? "lg:col-span-3" : ""}>
                    <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-3">Wins This Period</div>
                    <WinsFeed wins={wins} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <ColdReadsSection coldReads={reportData?.coldReads || []} data={data} prevData={prevData} competitors={competitors} />
        </div>

        {reportData?.customerJourney && (
          <div className="mb-4">
            <CustomerJourneySection journey={reportData.customerJourney} />
          </div>
        )}

        {(bestGrid || worstGrid) && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {bestGrid && (
                <MiniGrid gridData={bestGrid.gridData} label="Best" keyword={bestGrid.keyword}
                  visibility={bestGrid.visibility} prevVisibility={bestGrid.prevVisibility} hideNeg={hideNeg}
                  points={bestGrid.points} centerLat={bestGrid.centerLat} centerLng={bestGrid.centerLng} mapsLoaded={!!(window as any).google?.maps} />
              )}
              {worstGrid && (
                <MiniGrid gridData={worstGrid.gridData} label="Worst" keyword={worstGrid.keyword}
                  visibility={worstGrid.visibility} prevVisibility={worstGrid.prevVisibility} hideNeg={hideNeg}
                  points={worstGrid.points} centerLat={worstGrid.centerLat} centerLng={worstGrid.centerLng} mapsLoaded={!!(window as any).google?.maps} />
              )}
            </div>
          </>
        )}

        {neighborhoods.length > 0 && (
          <div className="mb-4">
            <SectionCard title="Neighborhood Rankings">
              <NeighborhoodMapSection neighborhoods={neighborhoods} mapCenter={mapCenter} />
            </SectionCard>
          </div>
        )}

        <div className="mb-4">
          <SectionCard title="Keyword Rankings"><KeywordTable keywords={keywords} hideNeg={hideNeg} /></SectionCard>
        </div>

        {reportData?.timeMachine && (
          <div className="mb-4">
            <TimeMachineSection data={reportData.timeMachine} />
          </div>
        )}

        {reportData?.revealSequence && (
          <div className="mb-4">
            <RevealSequence data={reportData.revealSequence} />
          </div>
        )}

        <div className="mb-4">
          <ProfileHealthAudit data={data} profileHealth={reportData?.profileHealth} />
        </div>

        <div className="report-card p-6 mb-4" data-testid="reviews-roadmap-section">
          <div className="text-[11px] font-semibold text-white/35 tracking-[0.12em] uppercase mb-4">Reviews, Roadmap & Jumpstart Checklist</div>

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 lg:gap-5 mb-5">
            <div>
              <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-3">Recent Reviews</div>
              <div className="flex flex-col gap-2.5 max-h-[350px] overflow-y-auto pr-1 custom-scrollbar">
                {reviews.length === 0 ? <div className="text-sm text-white/30">No reviews yet</div> :
                  reviews.map((r, i) => <ReviewCard key={i} review={r} />)}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-2">Domination Roadmap</div>
                <Roadmap currentStage={client.stage} />
              </div>

              <div>
                <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-2">30-Day Jumpstart</div>
                <ThirtyDayTracker data={data} jumpstart={jumpstart} isPublic={isPublic} />
              </div>

              <div>
                <div className="text-[12px] font-bold text-white/60 uppercase tracking-wider mb-2">Work Completed</div>
                <WorkLog items={workCompleted} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-white/[0.06]">
            {[
              { icon: "⭐", value: data.avgRating?.toFixed(1) || "N/A", label: "Avg Rating", color: "text-amber-400", bg: "bg-amber-500/10" },
              { icon: "💬", value: data.totalReviews || 0, label: "Total Reviews", color: "text-blue-400", bg: "bg-blue-500/10" },
              { icon: "✍️", value: data.posts || 0, label: "Posts Published", color: "text-cyan-400", bg: "bg-cyan-500/10" },
              { icon: "📸", value: data.photos || 0, label: "Photos Uploaded", color: "text-violet-400", bg: "bg-violet-500/10" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 flex items-center gap-3" data-testid={`summary-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center shrink-0 text-lg`}>
                  {stat.icon}
                </div>
                <div>
                  <div className={`text-[20px] font-extrabold font-['Outfit',sans-serif] leading-none ${stat.color}`}>{stat.value}</div>
                  <div className="text-[10px] text-white/35 mt-0.5 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {reportData?.plantedSeeds && reportData.plantedSeeds.length > 0 && (
          <div className="mb-4">
            <PlantedSeedsSection seeds={reportData.plantedSeeds} />
          </div>
        )}

        {reportData?.personalTouch && (
          <div className="mb-4">
            <PersonalTouchSection data={reportData.personalTouch} city={client.address?.split(",").slice(-2, -1)[0]?.trim() || ""} />
          </div>
        )}

        <div className="text-center text-[11px] text-white/15 mt-8 pb-8 font-medium tracking-wide">Powered by Digital Domination • Report generated automatically</div>
      </div>
    </div>
  );
}
